import * as THREE from 'three';
import {
  resetRenderedTiles,
  resetLoadedTilesNetwork,
  resetStats,
  setNumNodesLoading,
  setPointBudget,
  setPointLoadLimit,
  setFramenumber,
  pointBudget as pointBudgetValue,
  framenumber as framenumberValue,
  resourcePath as potreeResourcePath,
} from '../potreeState';
import { VRButton } from 'three/examples/jsm/webxr/VRButton.js';
import { CameraMode, ElevationGradientRepeat, LengthUnits } from '../defines';
import { EventDispatcher } from '../EventDispatcher';
import type { Listener } from '../EventDispatcher';
import { Features } from '../Features';
import { DeviceOrientationControls } from '../navigation/DeviceOrientationControls';
import { EarthControls } from '../navigation/EarthControls';
import { FirstPersonControls } from '../navigation/FirstPersonControls';
import { HeadlessOrbitController } from '../navigation/HeadlessOrbitController';
import { InputHandler } from '../navigation/InputHandler';
import { MainOrbitControllerShim } from '../navigation/MainOrbitControllerShim';
import { OrbitControls } from '../navigation/OrbitControls';
import { VRControls } from '../navigation/VRControls';
import { Renderer } from '../PotreeRenderer';
import { Utils } from '../utils';
import { PointCloudOctree } from '../PointCloudOctree';
import { AnnotationTool } from '../utils/AnnotationTool';
import { ClippingTool } from '../utils/ClippingTool';
import { Message } from '../utils/Message';
import { SphereVolume } from '../utils/Volume';
import { SelectionTool } from '../utils/SelectionTool';
import { TranslationService } from '../translation/TranslationService';
import { DynamicPointBudget } from '../utils/DynamicPointBudget';
import { DebugOverlay } from '../utils/DebugOverlay';
import { DebugTexturePlaneManager } from '../utils/DebugTexturePlane';
import { DebugTexturePlaneWidget } from '../utils/DebugTexturePlaneWidget';
import { FPSStatsWidget, FPSGraphWidget, FrameTimeWidget, PointBudgetGraphWidget, RendererInfoWidget } from '../utils/DebugWidgets';
import { PPCLoaderDebugWidget } from '../utils/PPCLoaderDebugWidget';
import { ColorOverrideWidget } from '../utils/ColorOverrideWidget';
import { FloorExportDebugWidget } from '../utils/FloorExportDebugWidget';
import { XrayDensityDebugWidget } from '../utils/XrayDensityDebugWidget';
import { DEFAULT_DENSITY_ALPHA } from '../materials/PointCloudMaterial';
import { initManifold } from '../utils/manifoldCsg';
import { PersonaManager } from '../personas/PersonaManager';
import { FloorplanRepresentationManager } from './FloorplanRepresentationManager';
import { BackdropCapture } from '../utils/BackdropContrast';
import { RoomLabelsLayer } from '../utils/RoomLabelsLayer';
import { MonitoringPersonaConfig, FloorplanPersonaConfig, SurfacePersonaConfig, LayoutPersonaConfig } from '../personas/configs';
import { buildClipState, applyClipState, mergeClipFragment } from './buildClipState';
import { ALL_OVERLAYS_ON } from './RenderPipeline';
import type { RenderPipeline, RenderPipelineContext } from './RenderPipeline';
import type { RenderView } from './RenderView';
import type { WallSketchEditSession } from '../personas/tools/implementations/WallSketchEditSession';
import { RenderViewState } from './RenderViewState';
import { applyOverrides } from './MaterialOverrideStack';
import { applyAnnotationBlacklist } from './AnnotationBlacklistStack';
import { makeDefaultSettingsBag, resolveSettings } from './RenderViewSettings';
import { EDLRenderer } from './EDLRenderer';
import { HQSplatRenderer } from './HQSplatRenderer';
import { Layer, MapOverlay } from './mapOverlay';
import { NavigationCube } from './NavigationCube';
import { PotreeRenderer, densityAccumulationType } from './PotreeRenderer';
import { Scene } from './Scene';
import { ClassificationsInfo } from '../materials/Classifications';
import { SourcesInfo } from '../materials/Sources';
import { updatePointClouds } from '../Potree_update_visibility';
import { FloorplanTool } from '../utils/FloorplanTool';
import { FloorIsolationManager, FloorIsolationDebugWidget } from '../utils/FloorIsolation';
import { ScreenshotRenderer } from '../utils/ScreenshotRenderer';
import { ViewerMode } from './ViewerMode';
import { CameraSettingsManager } from '../personas/CameraSettingsManager';
import { CameraOrientationManager } from '../personas/CameraOrientationManager';
import workerResponseQueue from '../gemoetries/PPCPointCloudGeometry/WorkerResponseQueue';
import unifiedWorkerPool from '../utils/workers/UnifiedWorkerPool';
import { clearHierarchyLoadQueue } from '../gemoetries/PPCPointCloudGeometry/PPCPointCloudGeometryNode';

/** Declare jQuery as global - used extensively in Potree for DOM manipulation */
declare const $: (selector: string | Element | (() => void)) => JQueryLike;

/** Minimal jQuery-like interface for Potree's DOM usage */
interface JQueryLike {
  [index: number]: HTMLElement;
  length: number;
  find(selector: string): JQueryLike;
  append(content: JQueryLike | string): JQueryLike;
  prepend(content: JQueryLike | string): JQueryLike;
  empty(): JQueryLike;
  get(index: number): HTMLElement;
  html(content: string): JQueryLike;
  detach(): JQueryLike;
  css(property: string, value: string): JQueryLike;
  click(handler: () => void): JQueryLike;
  slideToggle(duration: number): JQueryLike;
  animate(properties: Record<string, unknown>, duration: number): JQueryLike;
  dialog(): JQueryLike;
  addClass(className: string): JQueryLike;
}

/** Declare TWEEN as global - used for camera animations */
declare const TWEEN: {
  Easing: { Quartic: { Out: unknown } };
  Tween: new (obj: object) => TweenInstance;
  update(timestamp: number): void;
};

interface TweenInstance {
  to(target: object, duration: number): TweenInstance;
  easing(fn: unknown): TweenInstance;
  onUpdate(callback: () => void): TweenInstance;
  onComplete(callback: () => void): TweenInstance;
  start(): TweenInstance;
  stop(): void;
}

/** Declare global viewer variable used in some legacy code paths */
declare const viewer: Viewer;

/** Declare debugDisabled as optional global */
declare const debugDisabled: boolean | undefined;

const measureTimings = false;

/**
 * Overlay scenes the widget renders. Mostly the user's 3D content
 * (annotations, measurements, planes/volumes, floorplan) so the widget
 * shows the same scene content as the main view. Tool-specific overlays
 * (control gizmos, in-progress clip-volume meshes, debug helpers) stay
 * off — they're tied to the tool's active interaction in the main view.
 *
 * `toolOverlay` is on so in-progress tool previews (line prompts, plane
 * previews, floor previews) appear in the widget too. The pipeline
 * renders them with the widget's own camera, so they stay 3D.
 */
const WIDGET_OVERLAYS = {
  measurements: true,
  planes: true,
  volumes: true,
  floorplan: true,
  controls: false,
  clippingVolumeMesh: false,
  gizmo: false,
  toolOverlay: true,
  debug: false,
} as const;

const XRAY_DENSITY_ALPHA_STORAGE_KEY = 'pointorama.xrayDensityAlpha';

/** Stored X-ray density alpha, or the shader default when unset or invalid. */
function readStoredXrayDensityAlpha(): number {
  try {
    const stored = Number(localStorage.getItem(XRAY_DENSITY_ALPHA_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_DENSITY_ALPHA;
  } catch {
    return DEFAULT_DENSITY_ALPHA;
  }
}

export class Viewer extends EventDispatcher {
  renderArea: HTMLElement;
  // todo remove when implementing new designs
  guiLoaded: boolean;
  guiLoadTasks: (() => void)[];
  messages: Message[];
  elMessages: JQueryLike;
  // todo: remove, probably not used
  server: string | null = null;
  fov!: number;
  useDEMCollisions!: boolean;
  renderer!: THREE.WebGLRenderer;
  isSoftwareRenderer = false;
  isContextLost = false;
  /**
   * Viewing only (invited guests, share-link visitors). Tools keep selection and hover but
   * withhold every editing affordance: gizmos, body/handle drags, keyboard delete.
   * Set by the React page from RendererReadOnlyContext via setReadOnly().
   */
  readOnly = false;

  setReadOnly(readOnly: boolean): void {
    this.readOnly = readOnly;
  }
  dynamicPointBudget: DynamicPointBudget | null = null;
  debugOverlay: DebugOverlay | null = null;
  debugTexturePlanes: DebugTexturePlaneManager | null = null;
  orbitControls!: OrbitControls;
  controls!: OrbitControls | EarthControls | FirstPersonControls | DeviceOrientationControls | VRControls;
  measurementScene!: THREE.Scene;
  planeScene!: THREE.Scene;
  volumeScene!: THREE.Scene;
  /**
   * Top-most UI overlay scene. Rendered last of all (after a depth clear) so
   * its contents always draw over every other scene. Home for interactive
   * editor helpers (handles, hover/snap markers) that must never be hidden
   * behind world meshes like the floorplan skeleton.
   */
  uiScene!: THREE.Scene;
  /** Gizmo scene set by BoxPersonaTool when active, null otherwise */
  gizmoScene: THREE.Scene | null = null;
  /**
   * Scenes registered by active tools to render their interactive previews
   * (line prompts, plane creators, floor preview, etc.). Drawn by every
   * pipeline as part of the overlay pass, gated by `overlays.toolOverlay`,
   * so previews appear in widget render views with their own camera —
   * not stamped from the main view's camera.
   */
  toolOverlayScenes: THREE.Scene[] = [];
  selectionTool!: SelectionTool;
  floorplanTool!: FloorplanTool;
  floorIsolationManager!: FloorIsolationManager;
  screenshotRenderer!: ScreenshotRenderer;
  personaManager!: PersonaManager;
  /** Floorplan persona's ORIGINAL/SKINNED wall-representation switch state. */
  floorplanRepresentation!: FloorplanRepresentationManager;
  /** Framebuffer capture feeding the wall-skin lines' invert shader. */
  backdropCapture = new BackdropCapture();
  /** Name + area labels for room folders (floorplan persona, both representations). */
  roomLabelsLayer!: RoomLabelsLayer;
  translationService!: TranslationService;
  scene!: Scene;
  inputHandler!: InputHandler;
  lengthUnit!: { unitspermeter: number; code: string };
  lengthUnitDisplay!: { unitspermeter: number; code: string };
  classificationsInfo!: ClassificationsInfo;
  sourcesInfo!: SourcesInfo;
  pRenderer!: Renderer;
  /** Shared pipeline singletons. Per-view state lives on each RenderView. */
  potreePipeline!: PotreeRenderer;
  edlPipeline!: EDLRenderer;
  hqPipeline!: HQSplatRenderer;
  /** Active render views. Index 0 is conventionally the 'main' view. */
  renderViews!: RenderView[];
  beginTime: number;
  prevTime: number;
  frames: number;
  fps: number;
  instantFps: number;
  performanceCount: number;
  idle = false;
  originalCameraPositionZ!: number;
  originalCameraRotationZ!: number;
  mapOverlay!: MapOverlay;
  viewerMode!: ViewerMode;
  cameraSettingsManager!: CameraSettingsManager;
  cameraOrientationManager!: CameraOrientationManager;
  cachedPlaneAverageDistance = 1.0;
  private lastPlaneUpdateCameraPosition: THREE.Vector3 | null = null;
  private lastPlaneScale = -1;
  active2DPlane: THREE.Object3D | null = null; // Active plane when in 2D view mode, used for projecting annotations

  // Properties initialized in constructor body
  generateDEM!: boolean;
  minNodeSize!: number;
  edlStrength!: number;
  edlRadius!: number;
  edlOpacity!: number;
  useEDL!: boolean;
  description!: string;
  moveSpeed!: number;
  showBoundingBox!: boolean;
  showAnnotations!: boolean;
  freeze!: boolean;
  elevationGradientRepeat!: number;
  filterReturnNumberRange!: number[];
  filterNumberOfReturnsRange!: number[];
  filterGPSTimeRange!: number[];
  filterPointSourceIDRange!: number[];
  sceneVR!: THREE.Scene;
  overlay!: THREE.Scene | null;
  overlayCamera!: THREE.OrthographicCamera | null;
  clippingTool!: ClippingTool;
  navigationCube!: NavigationCube;
  skybox!: { camera: THREE.PerspectiveCamera; parent: THREE.Object3D; scene: THREE.Scene } | null;
  clock!: THREE.Timer;
  background!: string | null;
  _xrayTransparentPlane: THREE.Mesh | null = null;
  /** Per-point alpha for X-ray (density mode) rendering; see `xrayDensityAlpha`. */
  private _xrayDensityAlpha: number = readStoredXrayDensityAlpha();
  /** Per-skeleton 2D/3D skinning state saved on entering the plan view, so the
   * prior mode is restored on exit. Undefined when not in plan view. */
  _planViewPrevSkinning3D?: boolean[];
  /** Live `user_dark_mode` subscription held while the plan view is active,
   * so a theme toggle re-inverts the plan immediately. Undefined otherwise. */
  _planViewThemeSubscription?: { unsubscribe: () => void };
  /** The wall-sketch edit session currently in edit mode, if any. Registered
   * by the session on enter and cleared on exit, so the React property panel
   * can read and write the WORKING (uncommitted) skeleton state instead of
   * the persisted properties. */
  activeWallSketchEditSession: WallSketchEditSession | null = null;
  shadowTestCam!: THREE.PerspectiveCamera;
  fpControls!: FirstPersonControls;
  earthControls!: EarthControls;
  deviceControls!: DeviceOrientationControls;
  vrControls!: VRControls;
  scaleFactor!: number;
  onAnnotationAdded!: ((e: { annotation: { traverse(fn: (node: { domElement: JQueryLike; scene: Scene }) => void): void } }) => void) | null;
  annotationTool!: AnnotationTool;
  _previousCamera!: THREE.Camera;
  visibleAnnotations!: Set<unknown> | unknown[];
  useHQ!: boolean;
  toggle!: number;

  // Bound event handlers for cleanup
  private boundVisibilityChangeHandler: (() => void) | null = null;
  private boundBlurHandler: (() => void) | null = null;
  private boundFocusHandler: (() => void) | null = null;

  constructor(domElement: HTMLElement, args: { noDragAndDrop?: boolean; useDefaultRenderLoop?: boolean } = {}) {
    super();

    // CRITICAL: Dispose any existing viewer that might still be attached to window
    // This handles HMR/refresh scenarios where the old viewer wasn't properly cleaned up
    if (window.viewer && window.viewer !== (this as unknown as typeof window.viewer)) {
      console.warn('Disposing previous viewer instance before creating new one');
      try {
        (window.viewer as Viewer).dispose();
      } catch (e) {
        console.warn('Error disposing previous viewer:', e);
      }
      (window as { viewer: unknown }).viewer = null;
    }

    // CRITICAL: Clean up any stale resources from previous page loads/HMR
    // This prevents memory accumulation across refreshes
    unifiedWorkerPool.cleanup();
    clearHierarchyLoadQueue();
    workerResponseQueue.clear();

    // Clear global Potree state that may persist across refreshes
    resetRenderedTiles();
    resetLoadedTilesNetwork();
    setNumNodesLoading(0);
    resetStats();
    // Clear LRU cache and all nodes (don't create new instance)
    if (window.Potree?.lru?.dispose) {
      window.Potree.lru.dispose();
    }
    // Clear IndexedDB cache
    if (window.Potree?.ppcFileDb?.clear) {
      window.Potree.ppcFileDb.clear();
    }
    // Clear unified worker pool
    unifiedWorkerPool.cleanup();

    // Clean up any existing canvases/WebGL contexts in the DOM element
    // This handles cases where previous viewer wasn't properly disposed
    const existingCanvases = domElement.querySelectorAll('canvas');
    existingCanvases.forEach((canvas) => {
      const gl = (canvas as HTMLCanvasElement).getContext('webgl2') || (canvas as HTMLCanvasElement).getContext('webgl');
      if (gl) {
        const ext = gl.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
      }
      canvas.remove();
    });

    // Clean up old viewer elements while preserving important containers like olContainer
    // which is used by the map overlay and defined in the React component
    const childrenToRemove: ChildNode[] = [];
    domElement.childNodes.forEach((child) => {
      // Preserve the olContainer element used by map overlay
      if (child instanceof HTMLElement && child.id === 'olContainer') {
        return;
      }
      childrenToRemove.push(child);
    });
    childrenToRemove.forEach((child) => domElement.removeChild(child));

    this.renderArea = domElement;
    this.guiLoaded = false;
    this.guiLoadTasks = [];

    this.messages = [];
    this.elMessages = $(`
		<div id="message_listing" 
			style="position: absolute; z-index: 1000; left: 10px; bottom: 10px">
		</div>`);
    $(domElement).append(this.elMessages);

    this.fps = 0;
    this.instantFps = 0;
    this.beginTime = (performance || Date).now();
    this.prevTime = this.beginTime;
    this.frames = 0;
    this.performanceCount = 0;

    this.mapOverlay = new MapOverlay({ viewer: this });

    try {
      // generate missing dom hierarchy
      if ($(domElement).find('#potree_map').length === 0) {
        const potreeMap = $(`
					<div id="potree_map" class="mapBox" style="position: absolute; left: 50px; top: 50px; width: 400px; height: 400px; display: none">
						<div id="potree_map_header" style="position: absolute; width: 100%; height: 25px; top: 0px; background-color: rgba(0,0,0,0.5); z-index: 1000; border-top-left-radius: 3px; border-top-right-radius: 3px;">
						</div>
						<div id="potree_map_content" class="map" style="position: absolute; z-index: 100; top: 25px; width: 100%; height: calc(100% - 25px); border: 2px solid rgba(0,0,0,0.5); box-sizing: border-box;"></div>
					</div>
				`);
        $(domElement).append(potreeMap);
      }

      if ($(domElement).find('#potree_description').length === 0) {
        const potreeDescription = $(`<div id="potree_description" class="potree_info_text"></div>`);
        $(domElement).append(potreeDescription);
      }

      if ($(domElement).find('#potree_annotations').length === 0) {
        const potreeAnnotationContainer = $(`
					<div id="potree_annotation_container" 
						style="position: absolute; z-index: 100000; width: 100%; height: 100%; pointer-events: none;"></div>`);
        $(domElement).append(potreeAnnotationContainer);
      }

      if ($(domElement).find('#potree_quick_buttons').length === 0) {
        const potreeMap = $(`
					<div id="potree_quick_buttons" class="quick_buttons_container" style="">
					</div>
				`);

        $(domElement).append(potreeMap);
      }

      this.server = null;

      this.fov = 60;
      this.useDEMCollisions = false;
      this.generateDEM = false;
      this.minNodeSize = 30;
      this.edlStrength = 1.0;
      this.edlRadius = 1.4;
      this.edlOpacity = 1.0;
      this.useEDL = false;
      this.description = '';

      this.moveSpeed = 10;

      this.lengthUnit = LengthUnits.METER;
      this.lengthUnitDisplay = LengthUnits.METER;

      this.showBoundingBox = false;
      this.showAnnotations = true;
      this.freeze = false;

      this.elevationGradientRepeat = ElevationGradientRepeat.CLAMP;

      this.filterReturnNumberRange = [0, 7];
      this.filterNumberOfReturnsRange = [0, 7];
      this.filterGPSTimeRange = [-Infinity, Infinity];
      this.filterPointSourceIDRange = [0, 65535];

      this.renderer = null as unknown as THREE.WebGLRenderer;
      this.pRenderer = null as unknown as Renderer;

      this.scene = null as unknown as Scene;
      this.sceneVR = null as unknown as THREE.Scene;
      this.overlay = null;
      this.overlayCamera = null;

      this.inputHandler = null as unknown as InputHandler;
      this.controls = null as unknown as OrbitControls;

      this.clippingTool = null as unknown as ClippingTool;
      this.navigationCube = null as unknown as NavigationCube;

      this.skybox = null;
      this.clock = new THREE.Timer();
      this.clock.connect(document);
      this.background = null;

      this.initThree();

      if (!args.noDragAndDrop) {
        this.initDragAndDrop();
      }

      const canvas = this.renderer.domElement;
      canvas.addEventListener(
        'webglcontextlost',
        (e) => {
          // preventDefault is required for the browser to attempt a restore.
          e.preventDefault();
          this.isContextLost = true;
          this.postMessage('WebGL context lost. Attempting to recover\u2026');
          console.warn('WebGL context lost', e);
        },
        false,
      );
      canvas.addEventListener(
        'webglcontextrestored',
        () => {
          this.isContextLost = false;
          this.postMessage('WebGL context restored.');
          console.warn('WebGL context restored');
        },
        false,
      );

      this.overlay = new THREE.Scene();
      this.overlayCamera = new THREE.OrthographicCamera(0, 1, 1, 0, -1000, 1000);

      this.pRenderer = new Renderer(this.renderer);

      // Pipeline singletons. State is per-RenderView, not per-pipeline.
      this.potreePipeline = new PotreeRenderer();
      this.edlPipeline = new EDLRenderer(() => this.renderer ?? null);
      this.hqPipeline = new HQSplatRenderer();
      this.renderViews = [];

      const near = 2.5;
      const far = 10.0;

      this.shadowTestCam = new THREE.PerspectiveCamera(90, 1, near, far);
      this.shadowTestCam.position.set(3.5, -2.8, 8.561);
      this.shadowTestCam.lookAt(new THREE.Vector3(0, 0, 4.87));

      const scene = new Scene(this);
      this.sceneVR = new THREE.Scene();
      this.setScene(scene);

      {
        this.inputHandler = new InputHandler(this);
        this.inputHandler.setScene(this.scene);

        this.clippingTool = new ClippingTool(this);
        this.navigationCube = new NavigationCube(this);
        this.navigationCube.visible = false;

        this.createControls();

        this.clippingTool.setScene(this.scene);

        this.classificationsInfo = new ClassificationsInfo();
        this.sourcesInfo = new SourcesInfo();
        const onPointcloudAdded = (e: { pointcloud: { boundingBox: THREE.Box3; material: { densityAlpha: number } } }) => {
          // Clouds added after the setting was changed must pick it up too.
          e.pointcloud.material.densityAlpha = this._xrayDensityAlpha;
          if (this.scene.pointclouds.length === 1) {
            let speed = e.pointcloud.boundingBox.getSize(new THREE.Vector3()).length();
            speed = speed / 5;
            this.setMoveSpeed(speed);
          }
        };

        this.addEventListener('scene_changed', (e) => {
          this.inputHandler.setScene(e.scene);
          this.clippingTool.setScene(this.scene);

          if (!e.scene.hasEventListener('pointcloud_added', onPointcloudAdded as unknown as Listener)) {
            e.scene.addEventListener('pointcloud_added', onPointcloudAdded as unknown as Listener);
          }
        });

        this.scene.addEventListener('pointcloud_added', onPointcloudAdded as unknown as Listener);
      }

      // Store bound handlers so they can be removed in dispose()
      this.boundVisibilityChangeHandler = () => {
        this.idle = document.hidden || !document.hasFocus();
      };
      this.boundBlurHandler = () => {
        this.idle = true;
      };
      this.boundFocusHandler = () => {
        this.idle = false;
      };

      document.addEventListener('visibilitychange', this.boundVisibilityChangeHandler);
      window.addEventListener('blur', this.boundBlurHandler);
      window.addEventListener('focus', this.boundFocusHandler);

      {
        // set defaults
        this.setFOV(60);
        this.setEDLEnabled(true);
        this.setEDLRadius(1.4);
        this.setEDLStrength(0.4);
        this.setEDLOpacity(1.0);
        // Set point budget - start at middle of range and let dynamic adjustment find optimal
        const minPointBudget = 3 * 1000 * 1000;
        const maxPointBudget = this.isSoftwareRenderer ? 4 * 1000 * 1000 : 10 * 1000 * 1000;
        const initialPointBudget = Math.floor((minPointBudget + maxPointBudget) / 2);
        this.setPointBudget(initialPointBudget);

        // Enable dynamic point budget adjustment based on FPS
        this.enableDynamicPointBudget(minPointBudget, maxPointBudget);

        // Initialize debug overlay
        this.initDebugOverlay();

        this.setShowBoundingBox(false);
        this.setFreeze(false);
        this.setControls(this.orbitControls);

        // Set default background based on theme if available
        // This ensures the correct clear color from the very first frame
        if (typeof localStorage !== 'undefined') {
          if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
            this.setBackground('dark');
          } else {
            this.setBackground('light');
          }
        } else {
          this.setBackground('gradient');
        }

        this.scaleFactor = 1;

        this.loadSettingsFromURL();
      }

      this.renderer.setAnimationLoop(this.loop.bind(this));

      this.loadGUI = this.loadGUI.bind(this);
      this.toggleMapOverlay = this.toggleMapOverlay.bind(this);
      this.showMap = this.showMap.bind(this);
      this.hideMap = this.hideMap.bind(this);
      this.addWmsLayer = this.addWmsLayer.bind(this);
      this.removeWmsLayer = this.removeWmsLayer.bind(this);

      // Measurement overlay scene — rendered in a separate pass by PotreeRenderer/EDLRenderer
      this.measurementScene = new THREE.Scene();
      this.measurementScene.name = 'scene_measurement';
      this.inputHandler.registerInteractiveScene(this.measurementScene);

      // Plane/surface overlay scene — rendered in a separate pass by PotreeRenderer/EDLRenderer
      this.planeScene = new THREE.Scene();
      this.planeScene.name = 'scene_plane';

      // UI overlay scene — rendered LAST of all, after a depth clear, so its
      // contents (editor handles, hover/snap markers, gizmo helpers) always
      // draw on top of every other scene (point cloud, floorplan/skeleton
      // meshes, etc.) regardless of depth. Add interactive UI helpers here
      // instead of `scene.scene` when they must never be occluded.
      this.uiScene = new THREE.Scene();
      this.uiScene.name = 'scene_ui';

      // Volume overlay scene — viewer-owned so volumes render even when box tool is inactive
      this.volumeScene = new THREE.Scene();
      this.volumeScene.name = 'scene_volume';
      this.gizmoScene = null;
      this.initVolumeSceneSync();

      this.annotationTool = new AnnotationTool(this);
      this.selectionTool = new SelectionTool(this);
      this.floorplanTool = new FloorplanTool(this);
      this.floorIsolationManager = new FloorIsolationManager(this);
      this.screenshotRenderer = new ScreenshotRenderer(this);

      // Begin loading the Manifold WASM module used for floor opening cuts, so
      // it's ready before the first floor with doors/windows is built. Fire and
      // forget — the floor builder falls back to an uncut wall if it isn't
      // ready yet, then rebuilds once it resolves.
      void initManifold();

      // Initialize translation service
      this.translationService = new TranslationService();

      // Initialize persona system
      this.personaManager = new PersonaManager(this);
      this.floorplanRepresentation = new FloorplanRepresentationManager(this);
      this.roomLabelsLayer = new RoomLabelsLayer(this);

      // Register personas
      this.personaManager.registerPersona(MonitoringPersonaConfig);
      this.personaManager.registerPersona(FloorplanPersonaConfig);
      this.personaManager.registerPersona(SurfacePersonaConfig);
      this.personaManager.registerPersona(LayoutPersonaConfig);

      // Enable default persona (Monitoring)
      this.personaManager.enablePersona('monitoring');

      this.viewerMode = new ViewerMode(this);
      this.cameraSettingsManager = new CameraSettingsManager(this);
      this.cameraOrientationManager = new CameraOrientationManager(this, this.cameraSettingsManager);
    } catch (e) {
      this.onCrash(e as Error & { stack?: string });
    }
  }

  onCrash(error: Error & { stack?: string }) {
    $(this.renderArea).empty();

    if ($(this.renderArea).find('#potree_failpage').length === 0) {
      const elFailPage = $(`
			<div id="#potree_failpage" class="potree_failpage"> 
				
				<h1>Potree Encountered An Error </h1>

				<p>
				This may happen if your browser or graphics card is not supported.
				<br>
				We recommend to use 
				<a href="https://www.google.com/chrome/browser" target="_blank" style="color:initial">Chrome</a>
				or 
				<a href="https://www.mozilla.org/" target="_blank">Firefox</a>.
				</p>

				<p>
				Please also visit <a href="http://webglreport.com/" target="_blank">webglreport.com</a> and 
				check whether your system supports WebGL.
				</p>
				<p>
				If you are already using one of the recommended browsers and WebGL is enabled, 
				consider filing an issue report at <a href="https://github.com/potree/potree/issues" target="_blank">github</a>,<br>
				including your operating system, graphics card, browser and browser version, as well as the 
				error message below.<br>
				Please do not report errors on unsupported browsers.
				</p>

				<pre id="potree_error_console" style="width: 100%; height: 100%"></pre>
				
			</div>`);

      const elErrorMessage = elFailPage.find('#potree_error_console');
      elErrorMessage.html(error.stack ?? '');

      $(this.renderArea).append(elFailPage);
    }

    throw error;
  }

  // ------------------------------------------------------------------------------------
  // Viewer API
  // ------------------------------------------------------------------------------------

  setScene(scene: Scene) {
    if (scene === this.scene) {
      return;
    }

    const oldScene = this.scene;
    this.scene = scene;

    this.dispatchEvent({
      type: 'scene_changed',
      oldScene: oldScene,
      scene: scene,
    });

    // Annotations
    $('.annotation').detach();

    // for(let annotation of this.scene.annotations){
    //	this.renderArea.appendChild(annotation.domElement[0]);
    // }

    this.scene.annotations.traverse((annotation) => {
      this.renderArea.appendChild(annotation.domElement[0]);
    });

    if (!this.onAnnotationAdded) {
      this.onAnnotationAdded = (e) => {
        // console.log("annotation added: " + e.annotation.title);

        e.annotation.traverse((node) => {
          $('#potree_annotation_container').append(node.domElement);
          //this.renderArea.appendChild(node.domElement[0]);
          node.scene = this.scene;
        });
      };
    }

    if (oldScene) {
      oldScene.annotations.removeEventListener('annotation_added', this.onAnnotationAdded as unknown as Listener);
    }
    this.scene.annotations.addEventListener('annotation_added', this.onAnnotationAdded as unknown as Listener);
  }

  setControls(controls: OrbitControls | EarthControls | FirstPersonControls | DeviceOrientationControls | VRControls) {
    if (controls !== this.controls) {
      if (this.controls) {
        this.controls.enabled = false;
        this.inputHandler.removeInputListener(this.controls);
      }

      this.controls = controls;
      this.controls.enabled = true;
      this.inputHandler.addInputListener(this.controls);
    }
    this.dispatchEvent({ type: 'controls_changed' });
  }

  getControls() {
    if (this.renderer.xr.isPresenting) {
      return this.vrControls;
    } else {
      return this.controls;
    }
  }

  getMinNodeSize() {
    return this.minNodeSize;
  }

  setMinNodeSize(value: number) {
    if (this.minNodeSize !== value) {
      this.minNodeSize = value;
      this.dispatchEvent({ type: 'minnodesize_changed', viewer: this });
    }
  }

  getBackground() {
    return this.background;
  }

  setBackground(bg: string | null) {
    if (bg == null || this.background === bg) {
      return;
    }

    if (bg === 'skybox') {
      this.skybox = Utils.loadSkybox(potreeResourcePath + '/textures/skybox2/');
    }

    this.background = bg;
    this.dispatchEvent({ type: 'background_changed', viewer: this });
  }

  setDescription(value: string) {
    this.description = value;

    $('#potree_description').html(value);
    //$('#potree_description').text(value);
  }

  getDescription() {
    return this.description;
  }

  setShowBoundingBox(value: boolean) {
    if (this.showBoundingBox !== value) {
      this.showBoundingBox = value;
      this.dispatchEvent({ type: 'show_boundingbox_changed', viewer: this });
    }
  }

  getShowBoundingBox() {
    return this.showBoundingBox;
  }

  setMoveSpeed(value: number) {
    if (this.moveSpeed !== value) {
      this.moveSpeed = value;
      this.dispatchEvent({ type: 'move_speed_changed', viewer: this, speed: value });
    }
  }

  getMoveSpeed() {
    return this.moveSpeed;
  }

  setWeightClassification(w: number) {
    for (let i = 0; i < this.scene.pointclouds.length; i++) {
      this.scene.pointclouds[i].material.weightClassification = w;
      this.dispatchEvent({ type: 'attribute_weights_changed' + i, viewer: this });
    }
  }

  setFreeze(value: boolean | number) {
    value = Boolean(value);
    if (this.freeze !== value) {
      this.freeze = value;
      this.dispatchEvent({ type: 'freeze_changed', viewer: this });
    }
  }

  getFreeze() {
    return this.freeze;
  }

  setElevationGradientRepeat(value: number) {
    if (this.elevationGradientRepeat !== value) {
      this.elevationGradientRepeat = value;

      this.dispatchEvent({
        type: 'elevation_gradient_repeat_changed',
        viewer: this,
      });
    }
  }

  setPointBudget(value: number) {
    if (pointBudgetValue !== value) {
      setPointBudget(Math.floor(value));
      this.dispatchEvent({ type: 'point_budget_changed', viewer: this });
    }
  }

  getPointBudget() {
    return pointBudgetValue;
  }

  /**
   * Enable dynamic point budget adjustment based on FPS
   * @param minBudget - Minimum point budget (default: 100k)
   * @param maxBudget - Maximum point budget (default: 10M)
   */
  enableDynamicPointBudget(minBudget = 3000000, maxBudget = 10000000) {
    this.dynamicPointBudget = new DynamicPointBudget(minBudget, maxBudget);
  }

  /**
   * Disable dynamic point budget adjustment
   */
  disableDynamicPointBudget() {
    this.dynamicPointBudget = null;
    console.log('[Viewer] Dynamic point budget disabled');
  }

  /**
   * Check if dynamic point budget is enabled
   */
  isDynamicPointBudgetEnabled() {
    return this.dynamicPointBudget !== null;
  }

  /**
   * Get dynamic point budget statistics
   */
  getDynamicPointBudgetStats() {
    return this.dynamicPointBudget ? this.dynamicPointBudget.getStats() : null;
  }

  /**
   * Initialize the debug overlay with default widgets
   */
  initDebugOverlay() {
    try {
      this.debugOverlay = new DebugOverlay();

      // Add default widgets
      this.debugOverlay.addWidget(new FPSStatsWidget(this));
      this.debugOverlay.addWidget(new FPSGraphWidget(this));
      this.debugOverlay.addWidget(new FrameTimeWidget(this));
      this.debugOverlay.addWidget(new PointBudgetGraphWidget(this));
      this.debugOverlay.addWidget(new RendererInfoWidget(this));
      this.debugOverlay.addWidget(new PPCLoaderDebugWidget(this));

      // Initialize debug texture plane manager
      this.debugTexturePlanes = new DebugTexturePlaneManager(this);

      // Add debug texture plane widget
      this.debugOverlay.addWidget(new DebugTexturePlaneWidget(this.debugTexturePlanes));

      // Add color override widget for UI panel theming
      this.debugOverlay.addWidget(new ColorOverrideWidget());

      // Add floor isolation debug widget
      this.debugOverlay.addWidget(new FloorIsolationDebugWidget(() => this.floorIsolationManager));

      // Add floor export widget (exports selected floor's 3D mesh as OBJ)
      this.debugOverlay.addWidget(new FloorExportDebugWidget(() => this));

      // Add X-ray density alpha slider
      this.debugOverlay.addWidget(new XrayDensityDebugWidget(() => this));
    } catch (error) {
      console.error('[Viewer] Failed to initialize debug overlay:', error);
    }
  }

  /**
   * Per-point alpha used by every X-ray (density mode) view. Points stack
   * additively, so ~1/alpha overlapping points saturate a pixel to white.
   * Applied to every point cloud material (current and future) and persisted
   * in localStorage so a tuned value survives reloads.
   */
  get xrayDensityAlpha(): number {
    return this._xrayDensityAlpha;
  }

  set xrayDensityAlpha(value: number) {
    if (!Number.isFinite(value) || value <= 0) return;
    this._xrayDensityAlpha = value;
    for (const pointcloud of this.scene?.pointclouds ?? []) {
      pointcloud.material.densityAlpha = value;
    }
    try {
      localStorage.setItem(XRAY_DENSITY_ALPHA_STORAGE_KEY, String(value));
    } catch {
      // Storage unavailable (private mode / blocked): keep the in-memory value only.
    }
  }

  /**
   * Whether X-ray density is accumulated in a float target (no 1/255 floor on
   * `xrayDensityAlpha`). False on GPUs without float blending, where alphas
   * below ~0.002 have no visible effect.
   */
  get xrayDensityHighPrecision(): boolean {
    return densityAccumulationType(this.renderer.getContext()) !== null;
  }

  setShowAnnotations(value: boolean) {
    if (this.showAnnotations !== value) {
      this.showAnnotations = value;
      this.dispatchEvent({ type: 'show_annotations_changed', viewer: this });
    }
  }

  getShowAnnotations() {
    return this.showAnnotations;
  }

  setDEMCollisionsEnabled(value: boolean) {
    if (this.useDEMCollisions !== value) {
      this.useDEMCollisions = value;
      this.dispatchEvent({ type: 'use_demcollisions_changed', viewer: this });
    }
  }

  getDEMCollisionsEnabled() {
    return this.useDEMCollisions;
  }

  setEDLEnabled(value: boolean) {
    value = Boolean(value) && (Features?.SHADER_EDL?.isSupported() ?? false);

    if (this.useEDL !== value) {
      this.useEDL = value;
      this.dispatchEvent({ type: 'use_edl_changed', viewer: this });
    }
  }

  getEDLEnabled() {
    return this.useEDL;
  }

  setEDLRadius(value: number) {
    if (this.edlRadius !== value) {
      this.edlRadius = value;
      this.dispatchEvent({ type: 'edl_radius_changed', viewer: this });
    }
  }

  getEDLRadius() {
    return this.edlRadius;
  }

  setEDLStrength(value: number) {
    if (this.edlStrength !== value) {
      this.edlStrength = value;
      this.dispatchEvent({ type: 'edl_strength_changed', viewer: this });
    }
  }

  getEDLStrength() {
    return this.edlStrength;
  }

  setEDLOpacity(value: number) {
    if (this.edlOpacity !== value) {
      this.edlOpacity = value;
      this.dispatchEvent({ type: 'edl_opacity_changed', viewer: this });
    }
  }

  getEDLOpacity() {
    return this.edlOpacity;
  }

  /**
   * Enable or disable measurement depth fade globally.
   * When enabled, measurements behind the point cloud will fade via shader.
   */
  setMeasurementDepthFadeEnabled(enabled: boolean) {
    this.edlPipeline.setMeasurementDepthFadeEnabled(enabled);
    this.potreePipeline.setMeasurementDepthFadeEnabled(enabled);
  }

  setFOV(value: number) {
    if (this.fov !== value) {
      this.fov = value;
      this.dispatchEvent({ type: 'fov_changed', viewer: this });
    }
  }

  getFOV() {
    return this.fov;
  }

  disableAnnotations() {
    this.scene.annotations.traverse((annotation) => {
      annotation.domElement.css('pointer-events', 'none');

      // return annotation.visible;
    });
  }

  enableAnnotations() {
    this.scene.annotations.traverse((annotation) => {
      annotation.domElement.css('pointer-events', 'auto');

      // return annotation.visible;
    });
  }

  setFilterReturnNumberRange(from: number, to: number) {
    this.filterReturnNumberRange = [from, to];
    this.dispatchEvent({ type: 'filter_return_number_range_changed', viewer: this });
  }

  setFilterNumberOfReturnsRange(from: number, to: number) {
    this.filterNumberOfReturnsRange = [from, to];
    this.dispatchEvent({ type: 'filter_number_of_returns_range_changed', viewer: this });
  }

  setFilterGPSTimeRange(from: number, to: number) {
    this.filterGPSTimeRange = [from, to];
    this.dispatchEvent({ type: 'filter_gps_time_range_changed', viewer: this });
  }

  setFilterPointSourceIDRange(from: number, to: number) {
    this.filterPointSourceIDRange = [from, to];
    this.dispatchEvent({ type: 'filter_point_source_id_range_changed', viewer: this });
  }

  setLengthUnit(value: string) {
    switch (value) {
      case 'm':
        this.lengthUnit = LengthUnits.METER;
        this.lengthUnitDisplay = LengthUnits.METER;
        break;
      case 'ft':
        this.lengthUnit = LengthUnits.FEET;
        this.lengthUnitDisplay = LengthUnits.FEET;
        break;
      case 'in':
        this.lengthUnit = LengthUnits.INCH;
        this.lengthUnitDisplay = LengthUnits.INCH;
        break;
    }

    this.dispatchEvent({ type: 'length_unit_changed', viewer: this, value: value });
  }

  setLengthUnitAndDisplayUnit(lengthUnitValue: string, lengthUnitDisplayValue: string) {
    switch (lengthUnitValue) {
      case 'm':
        this.lengthUnit = LengthUnits.METER;
        break;
      case 'ft':
        this.lengthUnit = LengthUnits.FEET;
        break;
      case 'in':
        this.lengthUnit = LengthUnits.INCH;
        break;
    }

    switch (lengthUnitDisplayValue) {
      case 'm':
        this.lengthUnitDisplay = LengthUnits.METER;
        break;
      case 'ft':
        this.lengthUnitDisplay = LengthUnits.FEET;
        break;
      case 'in':
        this.lengthUnitDisplay = LengthUnits.INCH;
        break;
    }

    this.dispatchEvent({ type: 'length_unit_changed', viewer: this, value: lengthUnitValue });
  }

  /**
   * @param direction When provided, the view is reoriented to look along this
   *   world-space direction before framing. Used to face flat targets (e.g.
   *   wall openings) head-on instead of edge-on from the current view angle.
   *   Ignored in orthographic (2D) mode, which keeps its fixed top-down look.
   */
  zoomTo(node: THREE.Object3D & { boundingSphere?: THREE.Sphere; boundingBox?: THREE.Box3 }, factor: number, animationDuration = 0, direction?: THREE.Vector3) {
    const view = this.scene.view;

    // Orthographic (2D) mode is driven by view.radius (frustum half-width), not
    // by camera distance, so the perspective-based framing below does nothing
    // useful. Center the ortho view on the target and size the frustum to fit.
    if (this.scene.cameraMode === CameraMode.ORTHOGRAPHIC) {
      this.zoomToOrthographic(node, factor, animationDuration);
      return;
    }

    if (direction) {
      view.direction = direction.clone().normalize();
    }

    const camera = this.scene.cameraP.clone();
    camera.rotation.copy(this.scene.cameraP.rotation);
    camera.rotation.order = 'ZXY';
    camera.rotation.x = Math.PI / 2 + view.pitch;
    camera.rotation.y = 0;
    camera.rotation.z = view.yaw;
    // Apply roll around the camera's forward direction using quaternion
    if (view.roll !== 0) {
      const rollQuat = new THREE.Quaternion().setFromAxisAngle(view.direction, view.roll);
      camera.quaternion.premultiply(rollQuat);
    }
    camera.updateMatrix();
    camera.updateMatrixWorld();
    camera.zoomTo(node, factor);

    let bs: THREE.Sphere;
    const meshNode = node as THREE.Object3D & { geometry?: THREE.BufferGeometry; boundingSphere?: THREE.Sphere; boundingBox?: THREE.Box3 };
    if (meshNode.boundingSphere) {
      bs = meshNode.boundingSphere;
    } else if (meshNode.geometry && meshNode.geometry.boundingSphere) {
      bs = meshNode.geometry.boundingSphere;
    } else {
      bs = meshNode.boundingBox?.getBoundingSphere(new THREE.Sphere()) ?? new THREE.Sphere();
    }
    bs = bs.clone().applyMatrix4(node.matrixWorld);

    const startPosition = view.position.clone();
    const endPosition = camera.position.clone();
    const startTarget = view.getPivot();
    const endTarget = bs.center;

    const easing = TWEEN.Easing.Quartic.Out;

    {
      // animate camera position — registered on the view so a view switch
      // can cancel it instead of racing the new view's pose writes.
      const pos = startPosition.clone();
      const tween = view.registerMotionTween(new TWEEN.Tween(pos).to(endPosition, animationDuration));
      tween.easing(easing);

      tween.onUpdate(() => {
        view.position.copy(pos);
      });

      tween.start();
    }

    {
      // animate camera target
      const target = startTarget.clone();
      const tween = view.registerMotionTween(new TWEEN.Tween(target).to(endTarget, animationDuration));
      tween.easing(easing);
      tween.onUpdate(() => {
        view.lookAt(target);
      });
      tween.onComplete(() => {
        view.lookAt(target);
        this.dispatchEvent({ type: 'focusing_finished', target: this });
      });

      this.dispatchEvent({ type: 'focusing_started', target: this });
      tween.start();
    }
  }

  /**
   * Framing for orthographic (2D) mode. The ortho frustum is sized by
   * view.radius (half-width) and view.radius/aspect (half-height), so framing
   * means: keep the fixed 2D look direction, re-center the view over the
   * target, and grow view.radius until the target's on-screen footprint fits.
   */
  private zoomToOrthographic(node: THREE.Object3D & { boundingSphere?: THREE.Sphere; boundingBox?: THREE.Box3 }, factor: number, animationDuration: number) {
    const view = this.scene.view;

    node.updateMatrixWorld();
    const box = node.boundingBox?.clone().applyMatrix4(node.matrixWorld);
    if (!box || box.isEmpty()) return;
    const center = box.getBoundingSphere(new THREE.Sphere()).center;

    // Screen axes of the current 2D view. right = getSide(), up = right × dir.
    const dir = view.direction.clone().normalize();
    const right = view.getSide().normalize();
    const up = right.clone().cross(dir).normalize();

    // Project the box's 8 corners onto the screen axes to get its on-screen
    // half-extents (an opening is a thin vertical rectangle: top-down it barely
    // spans up/right, so this stays a gentle zoom rather than snapping to a line).
    const size = box.getSize(new THREE.Vector3());
    const min = box.min;
    let halfRight = 0;
    let halfUp = 0;
    for (let i = 0; i < 8; i++) {
      const corner = new THREE.Vector3(min.x + (i & 1 ? size.x : 0), min.y + (i & 2 ? size.y : 0), min.z + (i & 4 ? size.z : 0)).sub(center);
      halfRight = Math.max(halfRight, Math.abs(corner.dot(right)));
      halfUp = Math.max(halfUp, Math.abs(corner.dot(up)));
    }

    const aspect = this.scene.cameraP.aspect || 1;
    // radius = frustum half-width; half-height = radius / aspect. Require both to
    // cover the footprint, with margin so surrounding context (wall, adjacent
    // room) stays in frame rather than filling it with just the opening. Clamp so
    // a degenerate (edge-on) or tiny opening still zooms to a sensible level.
    const radius = Math.max(halfRight, halfUp * aspect, 1.0) * 3.0 * factor;

    // Re-center: keep the camera the same distance behind the pivot along the
    // look direction (position = pivot - dir * radius) so the target sits at the
    // center of the ortho frustum.
    const endRadius = radius;
    const endPosition = center.clone().sub(dir.clone().multiplyScalar(view.radius));

    const startPosition = view.position.clone();
    const startRadius = view.radius;
    const easing = TWEEN.Easing.Quartic.Out;

    if (animationDuration === 0) {
      view.position.copy(endPosition);
      view.radius = endRadius;
      this.dispatchEvent({ type: 'focusing_finished', target: this });
      return;
    }

    const value = { t: 0 };
    const tween = view.registerMotionTween(new TWEEN.Tween(value).to({ t: 1 }, animationDuration));
    tween.easing(easing);
    tween.onUpdate(() => {
      const t = value.t;
      view.position.copy(startPosition.clone().lerp(endPosition, t));
      view.radius = startRadius + (endRadius - startRadius) * t;
    });
    tween.onComplete(() => {
      view.position.copy(endPosition);
      view.radius = endRadius;
      this.dispatchEvent({ type: 'focusing_finished', target: this });
    });
    this.dispatchEvent({ type: 'focusing_started', target: this });
    tween.start();
  }

  /**
   * Stop every in-flight camera animation targeting the main view: motion
   * tweens registered on `scene.view` (setView / Utils.moveTo / zoomTo) and
   * the orbit controls' zoom-to-location tweens. Called at the start of each
   * view-switch pipeline so a stale tween can't keep writing the pose (up to
   * 600ms) after the switch has landed.
   */
  cancelCameraMotion(): void {
    this.scene.view.stopMotionTweens();
    this.orbitControls?.stopTweens();
  }

  moveToGpsTimeVicinity(time: number) {
    const result = Utils.findClosestGpsTime(time, this as unknown as Parameters<typeof Utils.findClosestGpsTime>[1]) as unknown as {
      position: THREE.Vector3;
      node: { pointcloud: { deepestNodeAt(pos: THREE.Vector3): { getBoundingBox(): THREE.Box3 } } };
    };

    const box = result.node.pointcloud.deepestNodeAt(result.position).getBoundingBox();
    const diameter = box.min.distanceTo(box.max);

    const camera = this.scene.getActiveCamera();
    const offset = camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(diameter);
    const newCamPos = result.position.clone().sub(offset);

    this.scene.view.position.copy(newCamPos);
    this.scene.view.lookAt(result.position);
  }

  showAbout() {
    $(function () {
      $('#about-panel').dialog();
    });
  }

  getBoundingBox(pointclouds?: PointCloudOctree[]) {
    return this.scene.getBoundingBox(pointclouds);
  }

  getGpsTimeExtent() {
    const range = [Infinity, -Infinity];

    for (const pointcloud of this.scene.pointclouds) {
      const attributes = pointcloud.pcoGeometry.pointAttributes.attributes;
      const aGpsTime = attributes.find((a) => a.name === 'gps-time');

      if (aGpsTime) {
        const rangeMin = Array.isArray(aGpsTime.range[0]) ? aGpsTime.range[0][0] : aGpsTime.range[0];
        const rangeMax = Array.isArray(aGpsTime.range[1]) ? aGpsTime.range[1][0] : aGpsTime.range[1];
        range[0] = Math.min(range[0], rangeMin);
        range[1] = Math.max(range[1], rangeMax);
      }
    }

    return range;
  }

  fitToScreen(factor = 1, animationDuration = 0) {
    const box = this.getBoundingBox(this.scene.pointclouds);

    const node = new THREE.Object3D() as THREE.Object3D & { boundingBox?: THREE.Box3 };
    node.boundingBox = box;

    this.zoomTo(node, factor, animationDuration);
    this.controls.stop();
  }

  toggleNavigationCube() {
    this.navigationCube.visible = !this.navigationCube.visible;
  }

  setView(view: string) {
    if (!view) return;

    switch (view) {
      case 'F':
        this.setFrontView();
        break;
      case 'B':
        this.setBackView();
        break;
      case 'L':
        this.setLeftView();
        break;
      case 'R':
        this.setRightView();
        break;
      case 'U':
        this.setTopView();
        break;
      case 'D':
        this.setBottomView();
        break;
    }
  }

  setTopView() {
    this.scene.view.yaw = 0;
    this.scene.view.pitch = -Math.PI / 2;

    this.fitToScreen();
  }

  setBottomView() {
    this.scene.view.yaw = -Math.PI;
    this.scene.view.pitch = Math.PI / 2;

    this.fitToScreen();
  }

  setFrontView() {
    this.scene.view.yaw = 0;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setBackView() {
    this.scene.view.yaw = Math.PI;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setLeftView() {
    this.scene.view.yaw = -Math.PI / 2;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setRightView() {
    this.scene.view.yaw = Math.PI / 2;
    this.scene.view.pitch = 0;

    this.fitToScreen();
  }

  setCameraMode(mode: number) {
    const modeChanged = this.scene.cameraMode !== mode;
    this.scene.cameraMode = mode;

    for (const pointcloud of this.scene.pointclouds) {
      pointcloud.material.useOrthographicCamera = mode === CameraMode.ORTHOGRAPHIC;
    }

    // The wall sketches' 3D extrusion is a 3D-only effect (see
    // PointoramaWallSketch.skinning3DEffective): entering an orthographic
    // view re-skins to the flat 2D representation and leaving restores the
    // extrusion — the floor's mesh3D property is untouched.
    if (modeChanged) {
      for (const sketch of this.scene.wallSketches ?? []) {
        sketch.updateSkinningVisualization();
      }
    }
  }

  getProjection() {
    const pointcloud = this.scene.pointclouds[0];

    if (pointcloud) {
      return pointcloud.projection;
    } else {
      return null;
    }
  }

  loadSettingsFromURL() {
    // Helper to safely get URL parameter as string
    const getParam = (name: string): string | null => Utils.getParameterByName(name) as string | null;

    // Legacy methods that may not exist on current Viewer class
    const legacyThis = this as unknown as Record<string, (val: unknown) => void>;

    if (getParam('pointSize')) {
      legacyThis.setPointSize(parseFloat(getParam('pointSize') as string));
    }

    if (getParam('FOV')) {
      this.setFOV(parseFloat(getParam('FOV') as string));
    }

    if (getParam('opacity')) {
      legacyThis.setOpacity(parseFloat(getParam('opacity') as string));
    }

    if (getParam('edlEnabled')) {
      const enabled = getParam('edlEnabled') === 'true';
      this.setEDLEnabled(enabled);
    }

    if (getParam('edlRadius')) {
      this.setEDLRadius(parseFloat(getParam('edlRadius') as string));
    }

    if (getParam('edlStrength')) {
      this.setEDLStrength(parseFloat(getParam('edlStrength') as string));
    }

    if (getParam('pointBudget')) {
      this.setPointBudget(parseFloat(getParam('pointBudget') as string));
    }

    if (getParam('showBoundingBox')) {
      const enabled = getParam('showBoundingBox') === 'true';
      if (enabled) {
        this.setShowBoundingBox(true);
      } else {
        this.setShowBoundingBox(false);
      }
    }

    if (getParam('material')) {
      const material = getParam('material');
      legacyThis.setMaterial(material);
    }

    if (getParam('pointSizing')) {
      const sizing = getParam('pointSizing');
      legacyThis.setPointSizing(sizing);
    }

    if (getParam('quality')) {
      const quality = getParam('quality');
      legacyThis.setQuality(quality);
    }

    if (getParam('position')) {
      let value = getParam('position') as string;
      value = value.replace('[', '').replace(']', '');
      const tokens = value.split(';');
      const x = parseFloat(tokens[0]);
      const y = parseFloat(tokens[1]);
      const z = parseFloat(tokens[2]);

      this.scene.view.position.set(x, y, z);
    }

    if (getParam('target')) {
      let value = getParam('target') as string;
      value = value.replace('[', '').replace(']', '');
      const tokens = value.split(';');
      const x = parseFloat(tokens[0]);
      const y = parseFloat(tokens[1]);
      const z = parseFloat(tokens[2]);

      this.scene.view.lookAt(new THREE.Vector3(x, y, z));
    }

    if (getParam('background')) {
      const value = getParam('background') as string;
      this.setBackground(value);
    }

    // if(Utils.getParameterByName("elevationRange")){
    //	let value = Utils.getParameterByName("elevationRange");
    //	value = value.replace("[", "").replace("]", "");
    //	let tokens = value.split(";");
    //	let x = parseFloat(tokens[0]);
    //	let y = parseFloat(tokens[1]);
    //
    //	this.setElevationRange(x, y);
    //	//this.scene.view.target.set(x, y, z);
    // }
  }

  // ------------------------------------------------------------------------------------
  // Viewer Internals
  // ------------------------------------------------------------------------------------

  createControls() {
    // create FIRST PERSON CONTROLS
    this.fpControls = new FirstPersonControls(this);
    this.fpControls.enabled = false;
    this.fpControls.addEventListener('start', this.disableAnnotations.bind(this));
    this.fpControls.addEventListener('end', this.enableAnnotations.bind(this));

    // create ORBIT CONTROLS
    this.orbitControls = new OrbitControls(this);
    this.orbitControls.enabled = false;
    this.orbitControls.addEventListener('start', this.disableAnnotations.bind(this));
    this.orbitControls.addEventListener('end', this.enableAnnotations.bind(this));

    // create EARTH CONTROLS
    this.earthControls = new EarthControls(this);
    this.earthControls.enabled = false;
    this.earthControls.addEventListener('start', this.disableAnnotations.bind(this));
    this.earthControls.addEventListener('end', this.enableAnnotations.bind(this));

    // create DEVICE ORIENTATION CONTROLS
    this.deviceControls = new DeviceOrientationControls(this);
    this.deviceControls.enabled = false;
    this.deviceControls.addEventListener('start', this.disableAnnotations.bind(this));
    this.deviceControls.addEventListener('end', this.enableAnnotations.bind(this));

    // create VR CONTROLS
    this.vrControls = new VRControls(this);
    this.vrControls.enabled = false;
    this.vrControls.addEventListener('start', this.disableAnnotations.bind(this));
    this.vrControls.addEventListener('end', this.enableAnnotations.bind(this));
  }

  async removeWmsLayer({ identifier }: { identifier: string }) {
    this.mapOverlay?.removeWmsLayer({ identifier });
  }

  async addWmsLayer(layer: Layer) {
    this.mapOverlay?.addWmsLayer(layer);
  }

  hideAllWmsLayers() {
    // @ts-expect-error - legacy method, may not exist on current MapOverlay
    this.mapOverlay?.hideAllWmsLayers?.();
  }

  showMap() {
    this.mapOverlay?.showMap();
  }

  hideMap() {
    this.mapOverlay?.hideMap();
  }

  toggleMapOverlay() {
    this.mapOverlay?.toggleMount();
  }

  onGUILoaded(callback: () => void) {
    if (this.guiLoaded) {
      callback();
    } else {
      this.guiLoadTasks.push(callback);
    }
  }

  promiseGuiLoaded() {
    return new Promise<void>((resolve) => {
      if (this.guiLoaded) {
        resolve();
      } else {
        this.guiLoadTasks.push(resolve);
      }
    });
  }

  loadGUI(callback?: () => void) {
    if (callback) {
      this.onGUILoaded(callback);
    }

    const elButtons = $('#potree_quick_buttons').get(0);

    const imgMapToggle = document.createElement('img');
    imgMapToggle.src = potreeResourcePath + '/icons/location.svg';
    imgMapToggle.style.display = 'none';
    imgMapToggle.id = 'potree_map_toggle';

    const imgMapOverlayToggle = document.createElement('img');
    imgMapOverlayToggle.src = potreeResourcePath + '/icons/map_icon.png';
    imgMapOverlayToggle.onclick = this.toggleMapOverlay;
    imgMapOverlayToggle.classList.add('potree_menu_toggle');

    elButtons.append(imgMapToggle);
    elButtons.append(imgMapOverlayToggle);

    if (navigator.xr) {
      navigator.xr.isSessionSupported('immersive-vr').then((supported) => {
        if (!supported) return;

        this.renderer.xr.enabled = true;
        const vrButton = VRButton.createButton(this.renderer) as HTMLElement & { element?: HTMLElement };
        const element = vrButton.element || vrButton;

        element.style.position = '';
        element.style.bottom = '';
        element.style.left = '';
        element.style.margin = '4px';
        element.style.fontSize = '100%';
        element.style.width = '2.5em';
        element.style.height = '2.5em';
        element.style.padding = '0';
        element.style.textShadow = 'black 2px 2px 2px';
        element.style.display = 'block';

        elButtons.append(element);
      });
    }

    $(() => {
      this.guiLoaded = true;
      for (const task of this.guiLoadTasks) {
        task();
      }
    });

    return this.promiseGuiLoaded();
  }

  setServer(server: string) {
    this.server = server;
  }

  initDragAndDrop() {
    function allowDrag(e: DragEvent) {
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      e.preventDefault();
    }

    const dropHandler = async (event: DragEvent) => {
      console.log(event);
      event.preventDefault();

      if (!event.dataTransfer) return;
      for (const item of event.dataTransfer.items) {
        console.log(item);

        if (item.kind !== 'file') {
          continue;
        }

        const file = item.getAsFile();
        if (!file) continue;

        const isGeoPackage = file.name.toLowerCase().endsWith('.gpkg');

        if (isGeoPackage) {
          console.warn('GeoPackage loading is no longer supported: GeoPackageLoader has been removed');
        }
      }
    };

    $('body')[0].addEventListener('dragenter', allowDrag);
    $('body')[0].addEventListener('dragover', allowDrag);
    $('body')[0].addEventListener('drop', dropHandler);
  }

  initThree() {
    const width = this.renderArea.clientWidth;
    const height = this.renderArea.clientHeight;

    const contextAttributes = {
      alpha: true,
      depth: true,
      stencil: false,
      antialias: false,
      //premultipliedAlpha: _premultipliedAlpha,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    };

    // let contextAttributes = {
    // 	alpha: false,
    // 	preserveDrawingBuffer: true,
    // };

    // let contextAttributes = {
    // 	alpha: false,
    // 	preserveDrawingBuffer: true,
    // };

    const canvas = document.createElement('canvas');

    const context = canvas.getContext('webgl2', contextAttributes) as WebGL2RenderingContext | null;

    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      premultipliedAlpha: false,
      canvas: canvas,
      context: context as unknown as WebGLRenderingContext,
      powerPreference: 'high-performance',
    });
    this.renderer.sortObjects = false;
    this.renderer.setSize(width, height);
    this.renderer.autoClear = false;
    this.renderArea.appendChild(this.renderer.domElement);
    this.renderer.domElement.tabIndex = 2222;
    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.addEventListener('mousedown', () => {
      this.renderer.domElement.focus();
    });
    //this.renderer.domElement.focus();

    // NOTE: If extension errors occur, pass the string into this.renderer.extensions.get(x) before enabling
    // enable frag_depth extension for the interpolation shader, if available
    const gl = this.renderer.getContext() as WebGL2RenderingContext;
    gl.getExtension('EXT_frag_depth');
    gl.getExtension('WEBGL_depth_texture');
    gl.getExtension('WEBGL_color_buffer_float'); // Enable explicitly for more portability, EXT_color_buffer_float is the proper name in WebGL 2

    if (gl.createVertexArray == null) {
      const extVAO = gl.getExtension('OES_vertex_array_object');

      if (!extVAO) {
        throw new Error('OES_vertex_array_object extension not supported');
      }

      gl.createVertexArray = extVAO.createVertexArrayOES.bind(extVAO);
      gl.bindVertexArray = extVAO.bindVertexArrayOES.bind(extVAO);
    }

    // Detect if using software rendering (CPU)
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      this.isSoftwareRenderer = renderer && (renderer.toLowerCase().includes('swiftshader') || renderer.toLowerCase().includes('software') || renderer.toLowerCase().includes('llvmpipe') || renderer.toLowerCase().includes('mesa'));

      if (this.isSoftwareRenderer) {
        console.warn('Software rendering detected:', renderer, '- Will use reduced point budget for better performance');
      }
    }
  }

  updateAnnotations() {
    if (!this.visibleAnnotations) {
      this.visibleAnnotations = new Set();
    }

    this.scene.annotations.updateBounds();
    this.scene.cameraP.updateMatrixWorld();
    this.scene.cameraO.updateMatrixWorld();

    const renderAreaSize = this.renderer.getSize(new THREE.Vector2());

    interface AnnotationNode {
      visible: boolean;
      scene: Scene;
      domElement: JQueryLike;
      position: THREE.Vector3 | null;
      offset: THREE.Vector3;
      boundingBox: THREE.Box3;
      children: unknown[];
      collapseThreshold?: number;
      descriptionVisible: boolean;
      expand: boolean;
      display: boolean;
      traverse(fn: (node: AnnotationNode) => boolean | void): void;
    }
    const visibleNow: AnnotationNode[] = [];
    (this.scene.annotations as unknown as AnnotationNode).traverse((annotation: AnnotationNode) => {
      if ((annotation as unknown) === this.scene.annotations) {
        return true;
      }

      if (!annotation.visible) {
        return false;
      }

      annotation.scene = this.scene;

      const element = annotation.domElement;

      let position: THREE.Vector3;
      if (annotation.position) {
        position = annotation.position.clone();
        position.add(annotation.offset);
      } else {
        position = annotation.boundingBox.getCenter(new THREE.Vector3());
      }

      const distance = this.scene.cameraP.position.distanceTo(position);
      const radius = annotation.boundingBox.getBoundingSphere(new THREE.Sphere()).radius;

      const screenPos = new THREE.Vector3();
      let screenSize = 0;

      // SCREEN POS
      screenPos.copy(position).project(this.scene.getActiveCamera());
      screenPos.x = (renderAreaSize.x * (screenPos.x + 1)) / 2;
      screenPos.y = renderAreaSize.y * (1 - (screenPos.y + 1) / 2);

      // SCREEN SIZE
      if (this.scene.cameraMode === CameraMode.PERSPECTIVE) {
        const fov = (Math.PI * this.scene.cameraP.fov) / 180;
        const slope = Math.tan(fov / 2.0);
        const projFactor = (0.5 * renderAreaSize.y) / (slope * distance);
        screenSize = radius * projFactor;
      } else {
        screenSize = Utils.projectedRadiusOrtho(radius, this.scene.cameraO.projectionMatrix, renderAreaSize.x, renderAreaSize.y);
      }

      element.css('left', screenPos.x + 'px');
      element.css('top', screenPos.y + 'px');
      //element.css("display", "block");

      let zIndex = 10000000 - distance * (10000000 / this.scene.cameraP.far);
      if (annotation.descriptionVisible) {
        zIndex += 10000000;
      }
      element.css('z-index', String(Math.floor(zIndex)));

      if (annotation.children.length > 0) {
        const expand = screenSize > (annotation.collapseThreshold ?? 0) || annotation.boundingBox.containsPoint(this.scene.getActiveCamera().position);
        annotation.expand = expand;

        if (!expand) {
          //annotation.display = (screenPos.z >= -1 && screenPos.z <= 1);
          const inFrustum = screenPos.z >= -1 && screenPos.z <= 1;
          if (inFrustum) {
            visibleNow.push(annotation);
          }
        }

        return expand;
      } else {
        //annotation.display = (screenPos.z >= -1 && screenPos.z <= 1);
        const inFrustum = screenPos.z >= -1 && screenPos.z <= 1;
        if (inFrustum) {
          visibleNow.push(annotation);
        }
      }
    });

    const notVisibleAnymore = new Set<AnnotationNode>(this.visibleAnnotations as Iterable<AnnotationNode>);
    for (const annotation of visibleNow) {
      annotation.display = true;

      notVisibleAnymore.delete(annotation);
    }
    this.visibleAnnotations = visibleNow;

    for (const annotation of notVisibleAnymore) {
      annotation.display = false;
    }
  }

  updateMaterialDefaults(pointcloud: { material: { intensityRange: number[] }; getAttribute(name: string): { range: number[] } | null }) {
    // PROBLEM STATEMENT:
    // * [min, max] of intensity, source id, etc. are computed as point clouds are loaded
    // * the point cloud material won't know the range it should use until some data is loaded
    // * users can modify the range at runtime, but sensible default ranges should be
    //   applied even if no GUI is present
    // * display ranges shouldn't suddenly change even if the actual range changes over time.
    //   e.g. the root node has intensity range [1, 478]. One of the descendants increases range to
    //   [0, 2047]. We should not automatically change to the new range because that would result
    //   in sudden and drastic changes of brightness. We should adjust the min/max of the sidebar slider.

    const material = pointcloud.material;

    const attIntensity = pointcloud.getAttribute('intensity');

    if (attIntensity != null && material.intensityRange[0] === 0 && material.intensityRange[1] === 0) {
      material.intensityRange = [...attIntensity.range];
    }

    // const attIntensity = pointcloud.getAttribute("intensity");
    // if(attIntensity && material.intensityRange[0] === Infinity){
    // 	material.intensityRange = [...attIntensity.range];
    // }

    // let attributes = pointcloud.getAttributes();

    // for(let attribute of attributes.attributes){
    // 	if(attribute.range){
    // 		let range = [...attribute.range];
    // 		material.computedRange.set(attribute.name, range);
    // 		//material.setRange(attribute.name, range);
    // 	}
    // }
  }

  /**
   * Gets the minimum plane radius based on point cloud spacing
   * @returns The minimum radius, defaults to 0.15 if no point cloud available
   */
  private getMinPlaneRadius(): number {
    const pointcloud = this.scene.pointclouds[0];
    const spacing = pointcloud?.pcoGeometry?.spacing ?? 0.1;
    // Minimum radius is 1.5x the spacing to ensure visibility
    return spacing * 0.2;
  }

  /**
   * Calculates the appropriate radius for a plane based on camera distance
   * @param plane - The plane object with getWorldPosition method
   * @returns The calculated radius scale factor
   */
  calculatePlaneRadius(_plane: THREE.Object3D): number {
    const camera = this.scene.getActiveCamera();
    const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
    const clientWidth = renderAreaSize.width;
    const clientHeight = renderAreaSize.height;

    // Use cached average distance instead of individual plane distance
    const pr = Utils.projectedRadius(1, camera, this.cachedPlaneAverageDistance, clientWidth, clientHeight);

    const minRadius = this.getMinPlaneRadius();
    return Math.max(Math.min(10 / pr, 10), minRadius);
  }

  invalidatePlaneScale() {
    this.lastPlaneScale = -1;
  }

  updatePlanes() {
    if (!this.scene) {
      return;
    }

    const planesCount = this.scene.planes.length;
    const surfacesCount = this.scene.surfaces.length;

    if (planesCount === 0 && surfacesCount === 0) {
      return;
    }

    const camera = this.scene.getActiveCamera();
    const cameraPos = camera.position;

    // Check if camera has moved significantly since last update
    // Skip expensive distance calculations if camera hasn't moved much
    const cameraMoveThreshold = 0.1; // 10cm threshold
    if (this.lastPlaneUpdateCameraPosition) {
      const cameraMoved = cameraPos.distanceTo(this.lastPlaneUpdateCameraPosition);
      if (cameraMoved < cameraMoveThreshold && this.lastPlaneScale > 0) {
        // Camera hasn't moved significantly, skip update
        return;
      }
    }

    // Update cached camera position
    if (!this.lastPlaneUpdateCameraPosition) {
      this.lastPlaneUpdateCameraPosition = new THREE.Vector3();
    }
    this.lastPlaneUpdateCameraPosition.copy(cameraPos);

    // Calculate distances only to closest few objects to reduce computation
    // We don't need exact distances for all objects, just a representative sample
    const totalObjects = planesCount + surfacesCount;
    const sampleSize = Math.min(totalObjects, 10); // Sample at most 10 objects
    const tempVec = new THREE.Vector3();

    let minDist = Infinity;
    let sumDist = 0;
    let countDist = 0;

    // Sample planes
    const planeStep = Math.max(1, Math.floor(planesCount / sampleSize));
    for (let i = 0; i < planesCount && countDist < sampleSize; i += planeStep) {
      const dist = cameraPos.distanceTo(this.scene.planes[i].getWorldPosition(tempVec));
      if (dist < minDist * 10) {
        // Filter outliers
        minDist = Math.min(minDist, dist);
        sumDist += dist;
        countDist++;
      }
    }

    // Sample surfaces
    const surfaceStep = Math.max(1, Math.floor(surfacesCount / sampleSize));
    for (let i = 0; i < surfacesCount && countDist < sampleSize; i += surfaceStep) {
      const dist = cameraPos.distanceTo(this.scene.surfaces[i].getWorldPosition(tempVec));
      if (dist < minDist * 10) {
        // Filter outliers
        minDist = Math.min(minDist, dist);
        sumDist += dist;
        countDist++;
      }
    }

    // Calculate average distance
    const averageDistance = countDist > 0 ? sumDist / countDist : 1.0;

    // Cache the average distance for use in calculatePlaneRadius
    this.cachedPlaneAverageDistance = averageDistance;

    // Calculate scale based on average distance
    const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
    const clientWidth = renderAreaSize.width;
    const clientHeight = renderAreaSize.height;
    const pr = Utils.projectedRadius(1, camera, averageDistance, clientWidth, clientHeight);
    const minRadius = this.getMinPlaneRadius();
    const scale = Math.max(Math.min(10 / pr, 10), minRadius);

    // Check if scale has changed significantly
    const scaleChangeThreshold = 0.01; // 1% change threshold
    if (Math.abs(scale - this.lastPlaneScale) / Math.max(scale, 0.001) < scaleChangeThreshold) {
      // Scale hasn't changed significantly, skip applying to objects
      return;
    }
    this.lastPlaneScale = scale;

    // Apply the same scale to all planes
    for (let i = 0; i < planesCount; i++) {
      const plane = this.scene.planes[i];
      plane.setRadius(scale);
      plane.radius = scale;
    }

    // Apply the same scale to all surfaces
    for (let i = 0; i < surfacesCount; i++) {
      const surface = this.scene.surfaces[i];
      surface.setRadius(scale);
      surface.radius = scale;
    }
  }

  updateRooms() {
    // Update all room visualizations (line material resolutions)
    for (const room of this.scene.rooms) {
      room.update();
    }
  }

  updateMeasurements() {
    for (const measure of this.scene.measurements) {
      measure.update();
    }
  }

  updateWallSketches() {
    // Update all skeleton visualizations (line material resolutions)
    for (const skeleton of this.scene.wallSketches) {
      skeleton.update();
    }
  }

  updateOutlines() {
    // Update all outline visualizations (line material resolutions). Without
    // this, Line2 lines render at the placeholder resolution and appear
    // invisible at most camera scales — matching the Room/WallSketch pattern.
    for (const outline of this.scene.outlines) {
      outline.update();
    }
  }

  updateFloorMeshes3D() {
    for (const mesh of this.scene.floorMeshes3D) {
      mesh.update();
    }
  }

  /** Sync viewer.scene.volumes ↔ volumeScene, and handle scene_changed. */
  private initVolumeSceneSync(): void {
    const onAdd = (e: { volume: THREE.Object3D }) => this.volumeScene.add(e.volume);
    const onRemove = (e: { volume: THREE.Object3D }) => this.volumeScene.remove(e.volume);

    // Sync existing volumes
    for (const volume of this.scene.volumes) {
      this.volumeScene.add(volume);
    }

    this.scene.addEventListener('volume_added', onAdd);
    this.scene.addEventListener('volume_removed', onRemove);

    // Re-attach on scene change
    this.addEventListener('scene_changed', (e: { oldScene?: Scene; scene?: Scene }) => {
      if (e.oldScene) {
        e.oldScene.removeEventListener('volume_added', onAdd);
        e.oldScene.removeEventListener('volume_removed', onRemove);
      }
      if (e.scene) {
        e.scene.addEventListener('volume_added', onAdd);
        e.scene.addEventListener('volume_removed', onRemove);
      }
    });
  }

  /** Update volume labels (size and text). Runs every frame; optional
   *  camera+size overrides allow per-RenderView rescaling. */
  updateVolumes(cameraOverride?: THREE.PerspectiveCamera | THREE.OrthographicCamera, widthOverride?: number, heightOverride?: number): void {
    if (!this.scene) return;

    const camera = cameraOverride ?? this.scene.getActiveCamera();
    let clientWidth: number, clientHeight: number;
    if (widthOverride !== undefined && heightOverride !== undefined) {
      clientWidth = widthOverride;
      clientHeight = heightOverride;
    } else {
      const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
      clientWidth = renderAreaSize.width;
      clientHeight = renderAreaSize.height;
    }

    for (const volume of this.scene.volumes) {
      const label = volume.label;
      const distance = label.position.distanceTo(camera.position);
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      const scale = 70 / pr;
      label.scale.set(scale, scale, scale);

      let calculatedVolume = volume.getVolume();
      calculatedVolume = (calculatedVolume / Math.pow(this.lengthUnit.unitspermeter, 3)) * Math.pow(this.lengthUnitDisplay.unitspermeter, 3);
      const text = Utils.addCommas(calculatedVolume.toFixed(3)) + ' ' + this.lengthUnitDisplay.code + '\u00B3';
      label.setText(text);
    }
  }

  /**
   * Rescale all label-bearing scene objects (volumes, measurements, rooms,
   * wallSketches) for the given camera + canvas size. Called from
   * `renderOneView` before each non-main view's pipeline render so widgets
   * see labels sized for their own camera/canvas.
   *
   * Last-write-wins on `label.scale`: widgets render first, main last. By
   * the end of the frame the labels are correctly sized for the main view
   * regardless of how many widgets ran in between.
   *
   * Skips the conditional optimizations from `viewer.update()` (camera-
   * change gating) since each per-view rescale is independent.
   */
  private rescaleLabelsForView(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera, width: number, height: number): void {
    if (!this.scene) return;

    // Labels + line-material resolutions: per-container, since each type
    // has its own structure of scale-able TextSprites.
    this.updateVolumes(camera, width, height);
    for (const measure of this.scene.measurements) {
      measure.update(camera, width, height);
    }
    for (const room of this.scene.rooms) {
      room.updateScales(camera, width, height);
    }
    for (const structure of this.scene.structures) {
      structure.updateScales(camera, width, height);
    }

    // SphereMarker handles: unified single pass. Any object with a
    // `.spheres: SphereMarker[]` field gets its handles rescaled here, so
    // we don't have to keep two copies of "loop my spheres and call
    // updateScale" inside each container class. Catches measurements,
    // rooms, wallSketches, and any future SphereMarker-bearing thing.
    const sphereOwners: Array<{ spheres?: { updateScale: (c?: THREE.PerspectiveCamera | THREE.OrthographicCamera, w?: number, h?: number) => void }[] }> = [
      ...this.scene.measurements,
      ...this.scene.rooms,
      ...this.scene.wallSketches,
    ];
    for (const owner of sphereOwners) {
      const spheres = owner.spheres;
      if (!spheres) continue;
      for (const sphere of spheres) {
        sphere.updateScale(camera, width, height);
      }
    }
  }

  update(delta: number, timestamp: number) {
    if (measureTimings) performance.mark('update-start');

    // Profiling timers
    const profileStart = performance.now();
    let materialsTime = 0;
    let controlsTime = 0;
    let clipBoxesTime = 0;
    let annotationsTime = 0;
    let planesTime = 0;

    this.dispatchEvent({
      type: 'update_start',
      delta: delta,
      timestamp: timestamp,
    });

    const scene = this.scene;
    const camera = scene.getActiveCamera();
    const visiblePointClouds = this.scene.pointclouds.filter((pc) => pc.visible);

    setPointLoadLimit(pointBudgetValue * 2);

    const lTarget = camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(1000));
    if (this.scene.directionalLight) {
      this.scene.directionalLight.position.copy(camera.position);
      this.scene.directionalLight.lookAt(lTarget);
    }

    const materialsStart = performance.now();
    for (const pointcloud of visiblePointClouds) {
      pointcloud.showBoundingBox = this.showBoundingBox;
      pointcloud.generateDEM = this.generateDEM;
      pointcloud.minimumNodePixelSize = this.minNodeSize;

      const material = pointcloud.material;

      material.uniforms.uFilterReturnNumberRange.value = this.filterReturnNumberRange;
      material.uniforms.uFilterNumberOfReturnsRange.value = this.filterNumberOfReturnsRange;
      material.uniforms.uFilterGPSTimeClipRange.value = this.filterGPSTimeRange;
      material.uniforms.uFilterPointSourceIDClipRange.value = this.filterPointSourceIDRange;

      material.classification = this.classificationsInfo.getSortedClassesForPointCloud(pointcloud.identifier ?? '');
      material.recomputeClassification();

      material.sources = this.sourcesInfo.getSortedSourceIdsForPointCloud(pointcloud.identifier ?? '');

      this.updateMaterialDefaults(pointcloud as Parameters<typeof this.updateMaterialDefaults>[0]);
    }
    materialsTime = performance.now() - materialsStart;

    if (this.showBoundingBox) {
      let bbRoot = this.scene.scene.getObjectByName('potree_bounding_box_root');
      if (!bbRoot) {
        const node = new THREE.Object3D();
        node.name = 'potree_bounding_box_root';
        this.scene.scene.add(node);
        bbRoot = node;
      }

      const visibleBoxes = [];
      for (const pointcloud of this.scene.pointclouds) {
        for (const node of pointcloud.visibleNodes.filter((vn) => (vn as unknown as { boundingBoxNode?: THREE.Object3D }).boundingBoxNode !== undefined)) {
          const box = (node as unknown as { boundingBoxNode: THREE.Object3D }).boundingBoxNode;
          visibleBoxes.push(box);
        }
      }

      bbRoot.children = visibleBoxes;
    }

    const freezeBlockStart = performance.now();
    if (!this.freeze) {
      const updatePCStart = performance.now();
      // Feed every enabled RenderView's camera into the LOD pass. The main
      // camera is index 0 (always present); widgets append their cameras
      // here so a node visible in any view stays loaded. domHeight per
      // camera drives screen-pixel weighting independently per canvas.
      const visibilityCameras: Parameters<typeof updatePointClouds>[1] = [{ camera, domHeight: this.renderer.domElement.clientHeight }];
      for (let i = 1; i < this.renderViews.length; i++) {
        const rv = this.renderViews[i];
        if (!rv.enabled) continue;
        visibilityCameras.push({
          camera: rv.camera as Parameters<typeof updatePointClouds>[1][number]['camera'],
          domHeight: rv.canvas.clientHeight || this.renderer.domElement.clientHeight,
        });
      }
      const result = updatePointClouds(scene.pointclouds as unknown as Parameters<typeof updatePointClouds>[0], visibilityCameras, this.renderer);
      this.frameProfile.updatePointClouds = performance.now() - updatePCStart;

      // If the main view has an active cameraClipOverride (e.g. wall focus
      // installed by a tool), honor it and skip the cloud-bbox auto-fit —
      // the override is intentionally tight around a slab and any wider
      // auto-fit would wash it out.
      const mainCameraClipOverride = this.renderViews[0]?.cameraClipOverride ?? null;
      if (mainCameraClipOverride) {
        camera.near = mainCameraClipOverride.near;
        camera.far = mainCameraClipOverride.far;
      } else if (result.lowestSpacing !== Infinity) {
        let near = result.lowestSpacing * 10.0;
        let far = -this.getBoundingBox().applyMatrix4(camera.matrixWorldInverse).min.z;

        far = Math.max(far * 1.5, 10000);
        near = Math.min(100.0, Math.max(0.01, near));
        far = Math.max(far, near + 10000);

        if (near === Infinity) {
          near = 0.1;
        }

        camera.near = near;
        camera.far = far;
      } else {
        // don't change near and far in this case
      }

      if (this.scene.cameraMode === CameraMode.ORTHOGRAPHIC && !mainCameraClipOverride) {
        camera.near = -camera.far;
      }
    }
    const freezeBlockTime = performance.now() - freezeBlockStart;

    this.scene.cameraP.fov = this.fov;

    const controlsStart = performance.now();
    const controls = this.getControls();
    if (controls === this.deviceControls) {
      this.controls.setScene(scene);
      this.controls.update(delta);

      this.scene.cameraP.position.copy(scene.view.position);
      this.scene.cameraO.position.copy(scene.view.position);
    } else if (controls !== null) {
      controls.setScene(scene);
      controls.update(delta);

      if (typeof debugDisabled === 'undefined') {
        this.scene.cameraP.position.copy(scene.view.position);
        // Build rotation: first apply pitch and yaw, then roll around the look direction
        // Start with pitch and yaw using Euler angles
        this.scene.cameraP.rotation.order = 'ZXY';
        this.scene.cameraP.rotation.x = Math.PI / 2 + this.scene.view.pitch;
        this.scene.cameraP.rotation.y = 0;
        this.scene.cameraP.rotation.z = this.scene.view.yaw;
        // Apply roll around the camera's forward direction using quaternion
        if (this.scene.view.roll !== 0) {
          const rollQuat = new THREE.Quaternion().setFromAxisAngle(scene.view.direction, this.scene.view.roll);
          this.scene.cameraP.quaternion.premultiply(rollQuat);
        }
      }

      this.scene.cameraO.position.copy(scene.view.position);
      // Build rotation: first apply pitch and yaw, then roll around the look direction
      this.scene.cameraO.rotation.order = 'ZXY';
      this.scene.cameraO.rotation.x = Math.PI / 2 + this.scene.view.pitch;
      this.scene.cameraO.rotation.y = 0;
      this.scene.cameraO.rotation.z = this.scene.view.yaw;
      // Apply roll around the camera's forward direction using quaternion
      if (this.scene.view.roll !== 0) {
        const rollQuat = new THREE.Quaternion().setFromAxisAngle(scene.view.direction, this.scene.view.roll);
        this.scene.cameraO.quaternion.premultiply(rollQuat);
      }
    }

    camera.updateMatrix();
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    controlsTime = performance.now() - controlsStart;

    if (this._previousCamera === undefined) {
      this._previousCamera = this.scene.getActiveCamera().clone();
      this._previousCamera.rotation.copy(this.scene.getActiveCamera().rotation);
    }

    const cameraChanged = !this._previousCamera.matrixWorld.equals(camera.matrixWorld) || !this._previousCamera.projectionMatrix.equals(camera.projectionMatrix);
    if (cameraChanged) {
      this.dispatchEvent({
        type: 'camera_changed',
        previous: this._previousCamera,
        camera: camera,
      });
    }

    this._previousCamera = this.scene.getActiveCamera().clone();
    this._previousCamera.rotation.copy(this.scene.getActiveCamera().rotation);

    const clipBoxesStart = performance.now();
    {
      const clipState = buildClipState(this);
      // Persist the MAIN view's floor-isolation clip onto the materials so the
      // picking + LOD passes (which read the shared material clip state, not a
      // per-view override) see it. Non-main views compose their own fragment in
      // `renderOneView`. Null fragment ⇒ no-op.
      const mainFragment = this.renderViews[0]?.floorIsolation ?? null;
      const persistentClip = mergeClipFragment(clipState, mainFragment);
      applyClipState(visiblePointClouds, persistentClip, this.clippingTool.maxPolygonVertices);
    }
    clipBoxesTime = performance.now() - clipBoxesStart;

    for (const pointcloud of visiblePointClouds) {
      pointcloud.material.elevationGradientRepat = this.elevationGradientRepeat;
    }

    // update navigation cube
    this.navigationCube.update(camera.rotation);

    const annotationsStart = performance.now();
    this.updateAnnotations();
    annotationsTime = performance.now() - annotationsStart;

    const planesStart = performance.now();
    this.updatePlanes();
    // Only update measurement/room/skeleton scales when camera moved or items need position updates
    // (their updateScales() does per-label distance calculations that are expensive with many items)
    if (cameraChanged || this.scene.measurements.some((m) => m.needsUpdate)) {
      this.updateMeasurements();
    }
    if (cameraChanged) {
      this.updateRooms();
      this.updateWallSketches();
      this.updateOutlines();
      this.updateFloorMeshes3D();
      const labelViewport = this.renderer.getSize(new THREE.Vector2());
      this.roomLabelsLayer?.updateScales(camera, labelViewport.width, labelViewport.height);
    }
    this.updateVolumes();
    planesTime = performance.now() - planesStart;

    TWEEN.update(timestamp);

    // Store granular update profile data
    const profileEnd = performance.now();
    this.frameProfile.updateFreezeBlock = freezeBlockTime;
    this.frameProfile.updateMaterials = materialsTime;
    this.frameProfile.updateControls = controlsTime;
    this.frameProfile.updateClipBoxes = clipBoxesTime;
    this.frameProfile.updateAnnotations = annotationsTime;
    this.frameProfile.updatePlanes = planesTime;
    this.frameProfile.updateOther = profileEnd - profileStart - materialsTime - controlsTime - clipBoxesTime - annotationsTime - planesTime - freezeBlockTime;

    this.dispatchEvent({
      type: 'update',
      delta: delta,
      timestamp: timestamp,
    });

    if (!this.originalCameraPositionZ && camera.position.z !== 0) {
      this.originalCameraPositionZ = camera.position.z;
      this.originalCameraRotationZ = camera.rotation.z;
    }

    if (measureTimings) {
      performance.mark('update-end');
      performance.measure('update', 'update-start', 'update-end');
    }
  }

  /**
   * Append a non-main RenderView. Returns a function that removes it again.
   * Disposes the view's pipeline state on remove.
   *
   * Ensures the main view exists first — if a widget mounts before the
   * first render frame (e.g. cloud-synced visibility restores on page load
   * faster than the renderer starts ticking), the widget would otherwise
   * land at index 0 and be treated as the main view by ensureMainView's
   * "renderViews is empty?" check.
   *
   * Cap on number of additional views: the shared renderer doesn't multiply
   * GL contexts, but each additional view allocates its own RTs. 4 widgets
   * is a comfortable upper bound at typical panel sizes.
   */
  addRenderView(view: RenderView): () => void {
    this.ensureMainView();
    if (view === this.renderViews[0]) {
      console.warn('addRenderView called with the main view; ignored');
      return () => undefined;
    }
    this.renderViews.push(view);
    return () => this.removeRenderView(view);
  }

  /**
   * Register a tool-owned scene to be rendered as part of every pipeline's
   * overlay pass (gated by `overlays.toolOverlay`). Tools used to render
   * these scenes themselves from a `render.pass.end` listener with
   * `scene.getActiveCamera()` — that bypassed the per-view camera and made
   * previews appear squished on widgets. Registering instead routes the
   * scene through the active pipeline's camera for each view.
   *
   * Returns an unregister function.
   */
  registerToolOverlayScene(scene: THREE.Scene): () => void {
    if (!this.toolOverlayScenes.includes(scene)) {
      this.toolOverlayScenes.push(scene);
    }
    return () => this.unregisterToolOverlayScene(scene);
  }

  unregisterToolOverlayScene(scene: THREE.Scene): void {
    const idx = this.toolOverlayScenes.indexOf(scene);
    if (idx >= 0) this.toolOverlayScenes.splice(idx, 1);
  }

  removeRenderView(view: RenderView): void {
    const idx = this.renderViews.indexOf(view);
    if (idx <= 0) return; // never remove main (idx 0) and ignore not-found (-1)
    this.renderViews.splice(idx, 1);
    try {
      view.pipeline.disposeState(view.pipelineState);
    } catch (e) {
      console.error('Error disposing pipeline state on RenderView removal:', e);
    }
  }

  /**
   * Pick the pipeline appropriate for the current `useHQ`/`useEDL` flags.
   * Used to seed the main view's pipeline; per-view pipeline overrides
   * (e.g. a widget that always uses Potree) bypass this.
   */
  private pickMainPipeline(): RenderPipeline {
    if (this.useHQ) {
      this.hqPipeline.useEDL = this.useEDL;
      return this.hqPipeline;
    }
    if (this.useEDL && Features?.SHADER_EDL?.isSupported()) {
      return this.edlPipeline;
    }
    return this.potreePipeline;
  }

  /**
   * Ensure the main view exists and its pipeline matches the current flags.
   * Lazy-creates the main view on first call so the constructor doesn't
   * have to know about RenderView state.
   */
  private ensureMainView(): RenderView {
    if (this.renderViews.length === 0) {
      const pipeline = this.pickMainPipeline();
      const main: RenderView = {
        id: 'main',
        enabled: true,
        canvas: this.renderer.domElement,
        // camera reference is replaced each frame from scene.getActiveCamera()
        camera: this.scene.getActiveCamera(),
        pipeline,
        pipelineState: pipeline.createState(),
        overlays: ALL_OVERLAYS_ON,
        clipOverride: null,
        materialOverride: {},
        settings: makeDefaultSettingsBag(),
        // Populated below after `main` is in hand — RenderViewState's
        // setters mutate this RenderView's camera/clip fields, so it
        // needs the reference. The main view's view-tab switching still
        // goes through the global ViewManager (the `ViewSelector`'s
        // `setView` path), but `customViews` (tool-spawned cards) live
        // on this `viewState`. Programmatic camera-pose / clip-override
        // commands also route through here (`focusOnWall`, etc.).
        viewState: null,
        cameraClipOverride: null,
        floorIsolation: null,
      };
      this.renderViews.push(main);

      // Wire a RenderViewState for the main view so tools can use the
      // widget's primitives (`focusOnWall`, `setClipOverride`, etc.) on
      // the main view too. The shim translates `RenderViewState`'s orbit
      // calls into mutations on `viewer.scene.view` (which the per-frame
      // writeback in `viewer.update()` then copies to the active camera).
      const mainOrbitShim = new MainOrbitControllerShim(this);
      const mainViewState = new RenderViewState(this, main, mainOrbitShim);
      main.viewState = mainViewState;
      this.floorIsolationManager?.attachView(main);

      return main;
    }

    const main = this.renderViews[0];
    const desired = this.pickMainPipeline();
    if (main.pipeline !== desired) {
      main.pipeline.disposeState(main.pipelineState);
      main.pipeline = desired;
      main.pipelineState = desired.createState();
    }
    return main;
  }

  /**
   * Lazily construct the persistent widget RenderView. Lives on the viewer
   * for the project's lifetime — independent of the React widget's visibility
   * — so tools that push state onto the widget's `RenderViewState` (e.g.
   * `DoorWindowTool`'s wall focus) keep applying even when the widget is
   * closed. On widget open, the React component calls
   * `viewState.attachOrbit` to wire its live canvas/camera/orbit onto this
   * persistent shell.
   *
   * Until the widget is first opened, the view sits with `enabled = false`,
   * a placeholder canvas (detached from the DOM), a placeholder camera, and
   * a `HeadlessOrbitController`. The per-frame render loop and the LOD pass
   * both skip disabled views, so the placeholder fields are never read.
   */
  ensureWidgetView(): RenderView {
    this.ensureMainView();
    for (let i = 1; i < this.renderViews.length; i++) {
      if (this.renderViews[i].id === 'widget-1') return this.renderViews[i];
    }

    // Placeholder canvas: never enters the DOM, never gets a context
    // requested. Holds the `view.canvas` slot so the type stays non-null
    // while the widget is detached. The React component swaps in the real
    // canvas via `attachOrbit` when it mounts.
    const placeholderCanvas = document.createElement('canvas');
    const placeholderCamera = new THREE.PerspectiveCamera(60, 1, 0.1, 10000);
    placeholderCamera.up.set(0, 0, 1);

    const pipeline = this.potreePipeline;
    const view: RenderView = {
      id: 'widget-1',
      enabled: false,
      canvas: placeholderCanvas,
      camera: placeholderCamera,
      pipeline,
      pipelineState: pipeline.createState(),
      overlays: WIDGET_OVERLAYS,
      clipOverride: null,
      materialOverride: {},
      settings: makeDefaultSettingsBag(),
      viewState: null,
      cameraClipOverride: null,
      floorIsolation: null,
    };

    const headlessOrbit = new HeadlessOrbitController();
    const viewState = new RenderViewState(this, view, headlessOrbit);
    view.viewState = viewState;

    this.renderViews.push(view);
    this.floorIsolationManager?.attachView(view);
    return view;
  }

  /**
   * Pick the pipeline appropriate for a widget's per-view EDL override (set
   * to `false` by X-Ray's view config in `RenderViewState.applyView`, etc.).
   * EDLRenderer hardcodes `material.useEDL = true` per frame, which breaks
   * X-Ray's density-mode shader path — the Potree fallback is required for
   * views that turn EDL off. Called from the widget's per-frame tick so the
   * pipeline swaps in lock-step with X-Ray transitions.
   */
  pickWidgetPipeline(view: RenderView): RenderPipeline {
    const perViewOverride = view.settings.useEDL.override.value;
    const wantEDL = perViewOverride !== undefined ? perViewOverride : this.useEDL;
    return wantEDL && Features?.SHADER_EDL?.isSupported() ? this.edlPipeline : this.potreePipeline;
  }

  renderVR() {
    console.log('RENDER VR WHUT');
    const renderer = this.renderer;

    renderer.setClearColor(0x550000, 0);
    renderer.clear();

    const xr = renderer.xr;
    const xrCameras = xr.getCamera();

    if (xrCameras.cameras.length !== 2) {
      return;
    }

    const makeCam = this.vrControls.getCamera.bind(this.vrControls);

    // clear framebuffer
    if (viewer.background === 'skybox') {
      renderer.setClearColor(0xff0000, 1);
    } else if (viewer.background === 'gradient') {
      renderer.setClearColor(0x112233, 1);
    } else if (viewer.background === 'black') {
      renderer.setClearColor(0x000000, 1);
    } else if (viewer.background === 'white') {
      renderer.setClearColor(0xffffff, 1);
    } else {
      renderer.setClearColor(0x000000, 0);
    }

    renderer.clear();

    // render background
    if (this.background === 'skybox' && this.skybox) {
      const skybox = this.skybox;

      const cam = makeCam();
      skybox.camera.rotation.copy(cam.rotation);
      skybox.camera.fov = cam.fov;
      skybox.camera.aspect = cam.aspect;

      // let dbg = new THREE.Object3D();
      const dbg = skybox.parent;
      // dbg.up.set(0, 0, 1);
      dbg.rotation.x = Math.PI / 2;

      // skybox.camera.parent = dbg;
      // dbg.children.push(skybox.camera);

      dbg.updateMatrix();
      dbg.updateMatrixWorld();

      skybox.camera.updateMatrix();
      skybox.camera.updateMatrixWorld();
      skybox.camera.updateProjectionMatrix();

      renderer.render(skybox.scene, skybox.camera);
      // renderer.render(skybox.scene, cam);
    } else if (this.background === 'gradient') {
      // renderer.render(this.scene.sceneBG, this.scene.cameraBG);
    }

    this.renderer.xr.getSession()?.updateRenderState({
      depthNear: 0.1,
      depthFar: 10000,
    });

    let cam = null;
    let view = null;

    {
      // render world scene
      cam = makeCam();
      cam.position.z -= 0.8 * cam.scale.x;
      cam.parent = null;
      // cam.near = 0.05;
      cam.near = viewer.scene.getActiveCamera().near;
      cam.far = viewer.scene.getActiveCamera().far;
      cam.updateMatrix();
      cam.updateMatrixWorld();

      this.scene.scene.updateMatrix();
      this.scene.scene.updateMatrixWorld();
      this.scene.scene.matrixAutoUpdate = false;

      const camWorld = cam.matrixWorld.clone();
      view = camWorld.clone().invert();
      this.scene.scene.matrix.copy(view);
      this.scene.scene.matrixWorld.copy(view);

      cam.matrix.identity();
      cam.matrixWorld.identity();
      cam.matrixWorldInverse.identity();

      renderer.render(this.scene.scene, cam);

      this.scene.scene.matrixWorld.identity();
    }

    for (const pointcloud of this.scene.pointclouds) {
      const viewport = xrCameras.cameras[0].viewport;

      pointcloud.material.useEDL = false;
      pointcloud.material.screenHeight = viewport.height;
      pointcloud.material.screenWidth = viewport.width;

      // automatically switch to paraboloids because they cause far less flickering in VR,
      // when point sizes are larger than around 2 pixels
      // if(Features.SHADER_INTERPOLATION.isSupported()){
      // 	pointcloud.material.shape = Potree.PointShape.PARABOLOID;
      // }
    }

    // render point clouds
    for (const xrCamera of xrCameras.cameras) {
      const v = xrCamera.viewport;
      renderer.setViewport(v.x, v.y, v.width, v.height);

      // xrCamera.fov = 90;

      {
        // estimate VR fov
        const proj = xrCamera.projectionMatrix;
        const inv = proj.clone().invert();

        const p1 = new THREE.Vector4(0, 1, -1, 1).applyMatrix4(inv);
        const rad = p1.y;
        const fov = 180 * (rad / Math.PI);

        xrCamera.fov = fov;
      }

      for (const pointcloud of this.scene.pointclouds) {
        const { material } = pointcloud;
        material.useEDL = false;
      }

      const vrWorld = view.clone().invert();
      vrWorld.multiply(xrCamera.matrixWorld);
      const vrView = vrWorld.clone().invert();

      this.pRenderer.render(this.scene.scenePointCloud, xrCamera, null, {
        viewOverride: vrView,
        orderedIdentifiers: this.scene.orderedIdentifiers || [],
      });
    }

    {
      // render VR scene
      const cam = makeCam();
      cam.parent = null;

      renderer.render(this.sceneVR, cam);
    }

    renderer.resetState();
  }

  renderDefault() {
    // Make sure the main view exists and its pipeline matches the current
    // useEDL/useHQ flags. Sync its camera to the active camera each frame.
    const main = this.ensureMainView();
    main.camera = this.scene.getActiveCamera();

    // Build the scene-derived clip state once per frame; views without an
    // override read this directly, views with `clipOverride !== null` get
    // their own state pushed onto materials inside applyOverrides.
    const baseClipState = buildClipState(this);

    // Render widgets FIRST, main view LAST. The shared renderer outputs to
    // the main canvas (this.renderer.domElement); widgets blit from there
    // to their own canvases mid-frame. Rendering main last guarantees the
    // main canvas's final contents are the main view.
    for (let i = 1; i < this.renderViews.length; i++) {
      const view = this.renderViews[i];
      if (!view.enabled) continue;
      this.renderOneView(view, baseClipState);
    }
    if (main.enabled) {
      this.renderOneView(main, baseClipState);
    }
  }

  private renderOneView(view: RenderView, baseClipState: ReturnType<typeof buildClipState>): void {
    const isMain = view === this.renderViews[0];
    // 1. Resize the shared renderer to this view's resolution. Drives RT sizes
    //    and screen-space material uniforms inside the pipeline.
    //
    //    For non-main views, pass updateStyle=false so we don't poke CSS
    //    width/height on the main canvas — only the intrinsic GL resolution
    //    needs to match the widget's resolution while we render into it.
    //    The main view restores both intrinsic size and CSS size each frame.
    //
    //    For the main view, source the size from `renderArea` (the layout
    //    container), not from `view.canvas` (the WebGL canvas). The canvas
    //    is absolute-positioned with an explicit CSS size that we set via
    //    `renderer.setSize(updateStyle=true)`, so its `clientWidth` only
    //    reflects whatever we wrote last frame — it never picks up the
    //    container resizing. Reading from the container restores the
    //    pre-multi-view resize behavior.
    const sizeSource = isMain ? this.renderArea : view.canvas;
    const width = this.scaleFactor * sizeSource.clientWidth;
    const height = this.scaleFactor * sizeSource.clientHeight;
    // Non-main views can briefly hit a 0×0 size — e.g. during the widget's
    // close-morph the container animates from panel→button rect, and the
    // canvas's `clientWidth/Height` can be 0 for a frame around the
    // unmount/detach boundary. Skip rendering then; `renderer.setSize(0, 0)`
    // would shrink the shared canvas to 0 and break the subsequent
    // `drawImage` blit (and main view's resize).
    if (!isMain && (width === 0 || height === 0)) return;
    this.renderer.setSize(width, height, isMain);
    const aspect = width / height;

    // 2. Update camera projections. The main view feeds back into scene cameras
    //    (preserves legacy behavior where cameraP.aspect / cameraO frustum are
    //    sized by the active render area).
    if (isMain) {
      const scene = this.scene;
      scene.cameraP.aspect = aspect;
      scene.cameraP.updateProjectionMatrix();
      const frustumScale = this.scene.view.radius;
      scene.cameraO.left = -frustumScale;
      scene.cameraO.right = frustumScale;
      scene.cameraO.top = (frustumScale * 1) / aspect;
      scene.cameraO.bottom = (-frustumScale * 1) / aspect;
      scene.cameraO.updateProjectionMatrix();
      scene.cameraScreenSpace.top = 1 / aspect;
      scene.cameraScreenSpace.bottom = -1 / aspect;
      scene.cameraScreenSpace.updateProjectionMatrix();
    } else if ((view.camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
      const cam = view.camera as THREE.PerspectiveCamera;
      cam.aspect = aspect;
      // Borrow `far` from the main camera, which the main view tunes per
      // frame against actual cloud bounds. Don't blindly copy `near` —
      // when the main view is in 2D mode (e.g. X-Ray), main's camera is
      // an OrthographicCamera and its `near` is set to `-far` (line 2072
      // in `viewer.update()`), which is fine for ortho but breaks a
      // perspective camera (negative near = degenerate projection,
      // nothing renders). Clamp near to a sane positive minimum.
      //
      // Also: skip the main borrow when the main view has its OWN
      // cameraClipOverride installed (e.g. wall focus). That override
      // narrows main's near/far to a wall-perpendicular slab that
      // makes no sense relative to the widget's camera pose, and
      // borrowing it clips the widget. Fall back to a cloud-bbox far.
      const mainCam = this.renderViews[0].camera as THREE.PerspectiveCamera;
      const mainHasClipOverride = this.renderViews[0]?.cameraClipOverride != null;
      if (view.cameraClipOverride) {
        cam.near = Math.max(0.01, view.cameraClipOverride.near);
        cam.far = view.cameraClipOverride.far;
      } else if (mainHasClipOverride) {
        // Main is in a tight-slab mode; derive our own loose far from
        // the cloud bbox so the widget keeps a sensible frustum.
        const bbox = this.getBoundingBox();
        const dist = bbox.getCenter(new THREE.Vector3()).distanceTo(cam.position);
        const diag = bbox.getSize(new THREE.Vector3()).length();
        cam.near = 0.1;
        cam.far = Math.max(10000, dist + diag * 2);
      } else {
        cam.far = mainCam.far;
        cam.near = Math.max(0.01, mainCam.near);
      }
      cam.updateProjectionMatrix();
      cam.updateMatrixWorld();
      cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
    } else if ((view.camera as THREE.OrthographicCamera).isOrthographicCamera) {
      // The widget orbit controller keeps top/bottom in sync with radius;
      // the only thing the renderer needs to maintain each frame is the
      // aspect-driven left/right (the canvas can resize independently).
      const cam = view.camera as THREE.OrthographicCamera;
      const halfH = (cam.top - cam.bottom) / 2;
      const halfW = halfH * aspect;
      cam.left = -halfW;
      cam.right = halfW;
      // Mirror the main view's ortho near/far convention: borrow `far`
      // from the main camera (tuned per frame to the cloud bounds), then
      // set `near = -far` so the visible Z range straddles the camera
      // plane symmetrically. Without this, scrolling/zooming in the
      // widget can push the near plane through the cloud and clip it.
      // Same skip-borrow rule as the perspective branch: when main has
      // a cameraClipOverride (wall focus), it's tight around a slab
      // relative to main's pose and would clip the widget.
      const mainCam = this.renderViews[0].camera as THREE.PerspectiveCamera;
      const mainHasClipOverride = this.renderViews[0]?.cameraClipOverride != null;
      if (view.cameraClipOverride) {
        cam.near = view.cameraClipOverride.near;
        cam.far = view.cameraClipOverride.far;
      } else if (mainHasClipOverride) {
        const bbox = this.getBoundingBox();
        const diag = bbox.getSize(new THREE.Vector3()).length();
        cam.far = Math.max(10000, diag * 2);
        cam.near = -cam.far;
      } else {
        cam.far = mainCam.far;
        cam.near = -cam.far;
      }
      cam.updateProjectionMatrix();
      cam.updateMatrixWorld();
      cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
    }

    // 3. Apply per-view material + clip overrides (snapshot-and-restore).
    //    Settings bag resolves to a MaterialOverride and (when the
    //    sceneClipMask hides any volume) a clipOverride. Imperative fields
    //    on the view (`view.materialOverride`, `view.clipOverride`) win on
    //    conflict — they're for code that wants to bypass the settings system.
    const resolved = resolveSettings(view.settings, baseClipState);
    const mergedMaterial = { ...resolved.materialOverride, ...view.materialOverride };
    // Compose this view's per-view floor-isolation clip into its clip override.
    // The MAIN view's fragment is already on the persistent material state (see
    // `viewer.update`), so only NON-main views fold it in here. A `{ mode:'none' }`
    // override (clipping fully off for the view) suppresses isolation too.
    let effectiveClipOverride = view.clipOverride ?? resolved.clipOverride;
    if (!isMain && view.floorIsolation && effectiveClipOverride?.mode !== 'none') {
      const baseState = effectiveClipOverride?.mode === 'replace' ? effectiveClipOverride.state : baseClipState;
      effectiveClipOverride = { mode: 'replace', state: mergeClipFragment(baseState, view.floorIsolation) };
    }
    const visiblePointClouds = this.scene.pointclouds.filter((pc) => pc.visible);
    const restore = applyOverrides(visiblePointClouds, mergedMaterial, effectiveClipOverride, baseClipState, this.clippingTool.maxPolygonVertices);
    // Per-view annotation blacklist: flip `.visible = false` on matching
    // annotations for the duration of this render only. Restored in the
    // `finally` below alongside the material-override restore.
    const annotationRestore = applyAnnotationBlacklist(this, view);

    // Floor 3D meshes (and scene.objects3DOnly) are 3D-only visualizations: suppress them for the
    // duration of any orthographic (2D) view's render — snapshot-and-restore
    // like the blacklist, so the persisted `floor_mesh3d_enabled` property is
    // untouched and 3D views (main or widget) still show the mesh.
    const viewIs2D = isMain ? this.scene.cameraMode === CameraMode.ORTHOGRAPHIC : (view.camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    const hiddenFloorMeshes: THREE.Object3D[] = [];
    if (viewIs2D) {
      for (const floorMesh of [...(this.scene.floorMeshes3D ?? []), ...(this.scene.objects3DOnly ?? [])]) {
        if (floorMesh.visible) {
          floorMesh.visible = false;
          hiddenFloorMeshes.push(floorMesh);
        }
      }
    }

    // Snapshot + override material.screenWidth/Height per view. The point
    // cloud shader reads these uniforms for adaptive point sizing — they're
    // pre-set in `updateMaterial` (called once per frame) using the renderer's
    // CSS-derived clientWidth (= main canvas size). For widget renders the
    // CSS size doesn't match the GL drawing buffer (we call setSize with
    // updateStyle=false), so the shader sees stale values and points are
    // sized for the wrong canvas. Snapshot/restore around each view's render
    // so each view gets its own dimensions while main's pre-set values are
    // restored before main renders.
    const screenSizeSnapshots: Array<{ material: { screenWidth: number; screenHeight: number }; w: number; h: number }> = [];
    for (const pc of visiblePointClouds) {
      const m = pc.material as unknown as { screenWidth: number; screenHeight: number };
      screenSizeSnapshots.push({ material: m, w: m.screenWidth, h: m.screenHeight });
      m.screenWidth = width;
      m.screenHeight = height;
    }

    // Rescale per-view labels. Single-valued `label.scale.x/y/z` is last-
    // write-wins; we render widgets first, main last, so the main view's
    // labels are correctly sized at end-of-frame regardless. The widget's
    // render sees widget-scaled labels because we rescale them right before.
    this.rescaleLabelsForView(view.camera, width, height);

    try {
      // 4. Build the per-view context.
      const ctx: RenderPipelineContext = {
        threeRenderer: this.renderer,
        pRenderer: this.pRenderer,
        viewer: this,
        camera: view.camera,
        width,
        height,
        clipSpheres: this.scene.volumes.filter((v) => v instanceof SphereVolume),
        overlays: view.overlays,
      };

      // 5. Run the pipeline.
      view.pipeline.clear(ctx, view.pipelineState);
      view.pipeline.render(ctx, view.pipelineState);

      // 6. Top-of-stack overlays — only on the main view (no widget UI yet).
      if (isMain) {
        if (this.overlay && this.overlayCamera) {
          this.renderer.render(this.overlay, this.overlayCamera);
        }
        if (view.overlays.debug) {
          this.renderer.render(this.scene.sceneDebug, view.camera);
        }
      }

      // 7. For non-main views, blit the rendered frame from the shared
      //    renderer's canvas onto this view's own canvas. The shared renderer
      //    can only output to one DOM canvas at a time; the 2D `drawImage`
      //    call is a GPU-side copy in modern browsers — cheap.
      //
      //    Setting canvas.width/height also clears the canvas to fully
      //    transparent, so we don't need a separate clearRect when the size
      //    matches a freshly-resized canvas. When sizes already match, the
      //    drawImage overwrites every pixel of the destination at full alpha
      //    (since the source is opaque from the pipeline's clear) — no
      //    accumulation. If the source happens to be transparent in places
      //    (e.g. background='transparent'), accumulation is still possible;
      //    explicit clearRect handles that case cheaply.
      if (!isMain) {
        if (view.canvas.width !== width) view.canvas.width = width;
        if (view.canvas.height !== height) view.canvas.height = height;
        const ctx2d = view.canvas.getContext('2d');
        if (ctx2d) {
          ctx2d.clearRect(0, 0, width, height);
          ctx2d.drawImage(this.renderer.domElement, 0, 0, width, height);
        }
      }
    } finally {
      // 7. Restore shared material/clip state. Mandatory — leaking corrupts
      //    the next view (or the next frame's main view).
      annotationRestore();
      restore();
      // Restore floor 3D meshes / 3D-only objects hidden for this 2D view's render.
      for (const floorMesh of hiddenFloorMeshes) {
        floorMesh.visible = true;
      }
      // Restore screen-size shader inputs.
      for (const snap of screenSizeSnapshots) {
        snap.material.screenWidth = snap.w;
        snap.material.screenHeight = snap.h;
      }
    }
  }

  render() {
    if (measureTimings) performance.mark('render-start');

    try {
      const vrActive = this.renderer.xr.isPresenting;

      if (vrActive) {
        this.renderVR();
      } else {
        this.renderDefault();
      }
    } catch (e) {
      this.onCrash(e as Error & { stack?: string });
    }

    if (measureTimings) {
      performance.mark('render-end');
      performance.measure('render', 'render-start', 'render-end');
    }
  }

  resolveTimings(timestamp: number) {
    if (measureTimings) {
      if (!this.toggle) {
        this.toggle = timestamp;
      }
      const duration = timestamp - this.toggle;
      if (duration > 1000.0) {
        const measures = performance.getEntriesByType('measure');

        interface TimingGroup {
          measures: { duration: number }[];
          sum: number;
          n: number;
          min: number;
          max: number;
          mean?: number;
          median?: number;
        }
        let names: Set<string> | string[] = new Set<string>();
        for (const measure of measures) {
          names.add(measure.name);
        }

        const groups = new Map<string, TimingGroup>();
        for (const name of names) {
          groups.set(name, {
            measures: [],
            sum: 0,
            n: 0,
            min: Infinity,
            max: -Infinity,
          });
        }

        for (const measure of measures) {
          const group = groups.get(measure.name);
          if (!group) continue;
          group.measures.push(measure);
          group.sum += measure.duration;
          group.n++;
          group.min = Math.min(group.min, measure.duration);
          group.max = Math.max(group.max, measure.duration);
        }

        const glQueries = new Map<string, number[]>(); // resolveQueries was removed
        for (const [key, value] of glQueries) {
          const group: TimingGroup = {
            measures: value.map((v: number) => {
              return { duration: v };
            }),
            sum: value.reduce((a: number, i: number) => a + i, 0),
            n: value.length,
            min: Math.min(...value),
            max: Math.max(...value),
          };

          const groupname = `[tq] ${key}`;
          groups.set(groupname, group);
          (names as Set<string>).add(groupname);
        }

        for (const [, group] of groups) {
          group.mean = group.sum / group.n;
          group.measures.sort((a: { duration: number }, b: { duration: number }) => a.duration - b.duration);

          if (group.n === 1) {
            group.median = group.measures[0].duration;
          } else if (group.n > 1) {
            group.median = group.measures[Math.floor(group.n / 2)].duration;
          }
        }

        const cn = Array.from(names as Set<string>).reduce((a: number, i: string) => Math.max(a, i.length), 0) + 5;
        const cmin = 10;
        const cmed = 10;
        const cmax = 10;
        const csam = 6;

        let message = ` ${'NAME'.padEnd(cn)} | ${'MIN'.padStart(cmin)} | ${'MEDIAN'.padStart(cmed)} | ${'MAX'.padStart(cmax)} | ${'SAMPLES'.padStart(csam)} \n`;
        message += ` ${'-'.repeat(message.length)}\n`;

        names = Array.from(names as Set<string>).sort();
        for (const name of names) {
          const group = groups.get(name);
          if (!group) continue;
          const min = group.min.toFixed(3);
          const median = (group.median ?? 0).toFixed(3);
          const max = group.max.toFixed(3);
          const n = group.n;

          message += ` ${name.padEnd(cn)} | ${min.padStart(cmin)} | ${median.padStart(cmed)} | ${max.padStart(cmax)} | ${n.toString().padStart(csam)}\n`;
        }
        message += `\n`;
        console.log(message);

        performance.clearMarks();
        performance.clearMeasures();
        this.toggle = timestamp;
      }
    }
  }

  // Frame profiling data - exposed for debug widgets
  frameProfile: {
    update: number;
    render: number;
    total: number;
    updatePointClouds: number; // Time spent in Potree.updatePointClouds
    betweenFrames: number; // Time between end of last frame and start of this frame
    workerProcess: number; // Time spent processing worker responses
    // Granular update breakdown
    updateFreezeBlock: number; // Full freeze block (includes updatePointClouds + getBoundingBox etc)
    updateMaterials: number;
    updateControls: number;
    updateClipBoxes: number;
    updateAnnotations: number;
    updatePlanes: number;
    updateOther: number;
  } = {
      update: 0,
      render: 0,
      total: 0,
      updatePointClouds: 0,
      betweenFrames: 0,
      workerProcess: 0,
      updateFreezeBlock: 0,
      updateMaterials: 0,
      updateControls: 0,
      updateClipBoxes: 0,
      updateAnnotations: 0,
      updatePlanes: 0,
      updateOther: 0,
    };
  private lastFrameEndTime = 0;

  loop(timestamp: number) {
    if (this.isContextLost) return;
    const loopStartTime = (performance || Date).now();

    // Calculate time between frames (captures work done outside render loop)
    if (this.lastFrameEndTime > 0) {
      this.frameProfile.betweenFrames = loopStartTime - this.lastFrameEndTime;
    }

    // Calculate instantaneous FPS from delta since last frame
    const deltaMs = loopStartTime - this.beginTime;
    if (deltaMs > 0) {
      this.instantFps = 1000 / deltaMs;
    }
    this.beginTime = loopStartTime;

    if (measureTimings) {
      performance.mark('loop-start');
    }

    const updateStart = performance.now();
    this.clock.update();
    this.update(this.clock.getDelta(), timestamp);
    const updateEnd = performance.now();

    const renderStart = performance.now();
    this.render();
    const renderEnd = performance.now();

    // Process worker responses with time budget to balance loading speed vs frame smoothness
    // Uses 8ms budget by default (half a frame at 60fps)
    const workerProcessTime = workerResponseQueue.processWithBudget();

    const frameEndTime = performance.now();

    // Store frame profile data
    this.frameProfile.update = updateEnd - updateStart;
    this.frameProfile.render = renderEnd - renderStart;
    this.frameProfile.workerProcess = workerProcessTime;
    this.frameProfile.total = frameEndTime - loopStartTime;

    // let vrActive = viewer.renderer.xr.isPresenting;
    // if(vrActive){
    // 	this.update(this.clock.getDelta(), timestamp);
    // 	this.render();
    // }else{

    // 	this.update(this.clock.getDelta(), timestamp);
    // 	this.render();
    // }

    if (measureTimings) {
      performance.mark('loop-end');
      performance.measure('loop', 'loop-start', 'loop-end');
    }

    this.resolveTimings(timestamp);

    setFramenumber(framenumberValue + 1);

    this.frames++;

    const time = (performance || Date).now();

    if (!this.idle) {
      // Calculate FPS every 3 seconds for display and legacy performance checks
      if (time > this.prevTime + 3000) {
        this.fps = (this.frames * 1000) / (time - this.prevTime);

        if (this.fps < 10) {
          this.performanceCount++;
          this.scene?.dispatchEvent({
            type: 'performance_check',
            value: { fps: this.fps, consecutiveHits: this.performanceCount },
          });
        } else this.performanceCount = 0;

        this.prevTime = time;
        this.frames = 0;
      }

      // Adjust point budget based on actual FPS performance
      if (this.dynamicPointBudget && this.fps > 0) {
        // Calculate total visible points across all point clouds
        let totalVisiblePoints = 0;
        if (this.scene && this.scene.pointclouds) {
          this.scene.pointclouds.forEach((pc: { numVisiblePoints?: number }) => {
            totalVisiblePoints += pc.numVisiblePoints || 0;
          });
        }

        const recommendedBudget = this.dynamicPointBudget.getRecommendedBudget(pointBudgetValue, this.fps, timestamp, totalVisiblePoints);

        if (recommendedBudget !== null) {
          this.setPointBudget(recommendedBudget);
        }
      }
    }

    // Record end time for between-frames tracking
    this.lastFrameEndTime = performance.now();
  }

  postError(content: string, params: { duration?: number } = {}) {
    const message = this.postMessage(content, params);

    message.element.addClass('potree_message_error');

    return message;
  }

  postMessage(content: string, params: { duration?: number } = {}) {
    const message = new Message(content);

    const animationDuration = 100;

    message.element.css('display', 'none');
    message.elClose.click(() => {
      message.element.slideToggle(animationDuration);

      const index = this.messages.indexOf(message);
      if (index >= 0) {
        this.messages.splice(index, 1);
      }
    });

    this.elMessages.prepend(message.element as unknown as string);

    message.element.slideToggle(animationDuration);

    this.messages.push(message);

    if (params.duration !== undefined) {
      const fadeDuration = 500;
      const slideOutDuration = 200;
      setTimeout(() => {
        message.element.animate(
          {
            opacity: 0,
          },
          fadeDuration,
        );
        message.element.slideToggle(slideOutDuration);
      }, params.duration);
    }

    return message;
  }

  getRenderPreview(): { primary: Blob; fallback: Blob } | null {
    const canvas = this.renderer.domElement;

    // Downscale to thumbnail size for fast loading on project list
    const maxWidth = 512;
    const scale = Math.min(1, maxWidth / canvas.width);
    const thumbWidth = Math.round(canvas.width * scale);
    const thumbHeight = Math.round(canvas.height * scale);

    const offscreen = document.createElement('canvas');
    offscreen.width = thumbWidth;
    offscreen.height = thumbHeight;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(canvas, 0, 0, thumbWidth, thumbHeight);

    const toBlob = (mime: string, quality: number): Blob => {
      const imgData = offscreen.toDataURL(mime, quality);
      const byteString = atob(imgData.split(',')[1]);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      return new Blob([uint8Array], { type: mime });
    };

    const supportsWebP = offscreen.toDataURL('image/webp').startsWith('data:image/webp');
    const primary = toBlob(supportsWebP ? 'image/webp' : 'image/jpeg', 0.8);
    const fallback = toBlob('image/jpeg', 0.8);

    return { primary, fallback };
  }

  /**
   * Dispose of the viewer and all associated resources.
   * Call this before destroying the viewer instance to prevent memory leaks.
   */
  dispose() {
    this.roomLabelsLayer?.dispose();
    this.floorplanRepresentation?.dispose();
    this.backdropCapture.dispose();

    // Remove global event listeners to prevent memory leaks
    if (this.boundVisibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChangeHandler);
      this.boundVisibilityChangeHandler = null;
    }
    if (this.boundBlurHandler) {
      window.removeEventListener('blur', this.boundBlurHandler);
      this.boundBlurHandler = null;
    }
    if (this.boundFocusHandler) {
      window.removeEventListener('focus', this.boundFocusHandler);
      this.boundFocusHandler = null;
    }

    // Dispose scene resources including geometry caches
    if (this.scene) {
      // Use removePointCloud to properly dispose each point cloud
      const identifiers = this.scene.pointclouds.map((pc) => pc.identifier).filter((id): id is string => id !== undefined);
      identifiers.forEach((id) => {
        this.scene.removePointCloud(id);
      });
    }

    // Cleanup workers and queues
    unifiedWorkerPool.cleanup();
    clearHierarchyLoadQueue();
    workerResponseQueue.clear();

    // Call Potree.cleanup if available (clears global state, IndexedDB, stats, LRU)
    if (window.Potree?.cleanup) {
      window.Potree.cleanup();
    }

    // Dispose Three.js renderer and force WebGL context loss
    if (this.renderer) {
      // Stop the animation loop first to prevent further rendering
      this.renderer.setAnimationLoop(null);

      // Clear render lists to release object references
      this.renderer.renderLists?.dispose();

      // Dispose the renderer
      this.renderer.dispose();

      // Force context loss to free GPU memory immediately
      this.renderer.forceContextLoss();

      // Remove the canvas from DOM
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }

    // Remove all remaining DOM elements
    if (this.renderArea) {
      // Dispose any remaining canvases' WebGL contexts
      const canvases = this.renderArea.querySelectorAll('canvas');
      canvases.forEach((canvas) => {
        const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
        if (gl) {
          const ext = gl.getExtension('WEBGL_lose_context');
          if (ext) ext.loseContext();
        }
      });

      while (this.renderArea.firstChild) {
        this.renderArea.removeChild(this.renderArea.firstChild);
      }
    }

    // Clear all event listeners on this viewer to prevent ghost callbacks
    // @ts-expect-error - accessing private _listeners from EventDispatcher to clear on dispose
    if (this._listeners) {
      // @ts-expect-error - see above
      this._listeners = {};
    }
  }
}
